import React, { useEffect, useRef, useState } from "react";
import { createChaacCanopyModel } from "./ChaacCanopyModel";
import { createChaacPowerSupply } from "./ChaacPowerSupply";
import { createControllerPowerSupply } from "./ControllerPowerSupply";
import { createControllerStandAssembly, controllerFocusTheta } from "./ControllerStandAssembly";
import { createMeasurementConnections } from "./MeasurementConnections";
import { createControllerBoardModel, controllerPortDetails, controllerAuxPort, controllerPowerInput, controllerPumpPort } from "./ControllerBoardModel";
import ControllerPortGuide, { ControllerPortDetail } from "./ControllerPortGuide";
import { microLineNozzle } from "./MicroLineNozzle";
import { createMicroLineNozzleAssembly } from "./MicroLineNozzleAssembly";
import { createBasilPlantAssets, createBasilBranch, basilBranchAnchorAtHeight } from "./BasilPlantGeometry";
import { createPumpNameplate, pumpNameplatePhoto, pumpNameplateNotice } from "./PumpNameplate";
import { createMrsPrefilterModel } from "./MrsPrefilterModel";
import { displayUvToPixel, findVisibleDisplayIntersection, isControllerDisplayFrame, controllerDisplayScreenshot, controllerDisplayScreenshotNotice } from "./ControllerDisplaySurface";
import { applySceneRenderQuality } from "./SceneRenderQuality";
import "./greenhouseScene3D.css";

export const sceneParts = [
  ["reservoir", "Tank & Filter", "hydraulics"], ["pump", "24-V-Druckpumpe", "hydraulics"],
  ["nozzle", "0,2-mm-Sprühdüsen", "hydraulics"], ["chambers", "AeroFlo 20 – Wurzelkammern", "hydraulics"],
  ["netpots", "Netztöpfe", "hydraulics"], ["roots", "Pflanzen & Wurzeln", "plants"],
  ["air", "Messeinrichtung", "climate"], ["sensors", "Messluft · Luftweg B", "climate"],
  ["controller", "STM32 & Touchdisplay", "electronics"], ["gateway", "ESP32-Gateway", "electronics"],
  ["chaac", "Chaac · VPD & VWC", "electronics"], ["microfilter", "Mikrofilter", "hydraulics"],
  ["uvc", "UVC-Behandlung", "hydraulics"], ["osmosis", "Waterdrop · Osmoseanlage", "hydraulics"],
  ["refill", "Tank nachfüllen · NC-Ventil", "hydraulics"], ["drain", "Tankablass · manuell", "hydraulics"],
  ["humidity1", "HUMIDITY 1", "climate"],
  ["humidity2", "HUMIDITY 2 · optional", "climate"], ["temperature", "Temperatur · Nährlösungstank", "climate"],
  ["ecIn", "EC-IN · Nährlösungstank", "hydraulics"], ["ecOut", "EC-OUT · Rücklauf vor UVC", "hydraulics"],
  ["leaf", "Leaf Rev4 · optional", "plants"]
];
const layerLabels = { hydraulics: "Nährlösung", climate: "Messluft", electronics: "Elektronik", plants: "Pflanzen" };
const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
const measurementPartIds = ["air", "sensors", "humidity1"];
const visibleControllerPorts = [...controllerPortDetails, controllerAuxPort, controllerPowerInput, controllerPumpPort];
// Presentation dimensions only. The shell is twice as high as the previous
// 3.8 / 5.7 drawing; none of the Aeroponic equipment is rescaled with it.
export const greenhouseEnvelope = Object.freeze({ eaves: 7.6, ridge: 11.4 });
export const greenhouseOverview = Object.freeze({ target: Object.freeze([0, 4.5, 0]), radius: 26, phi: 1.15 });
// Start with the equipment rather than an empty roof volume. The greenhouse
// envelope remains unchanged and can still be explored by orbiting/zooming out.
export const greenhousePresentation = Object.freeze({ target: Object.freeze([-.7, 1.3, 1.1]), radius: 15, phi: 1.12, theta: -.92 });
export const nozzleDetails = Array.from({ length: 9 }, (_, index) => ({
  id: `chamber-${Math.floor(index / 3) + 1}-nozzle-${index % 3 + 1}`,
  label: `Kammer ${Math.floor(index / 3) + 1} · Düse ${index % 3 + 1}`,
  orifice: "0,2 mm Düsenöffnung", pressure: "ca. 25 bar · Nutzerbeobachtung bei 12 Düsen", product: microLineNozzle
}));
export function NozzleDetailCard({ detail, onClose }) {
  return <aside className="feeder-scene3d-nozzle-detail" role="status" aria-label="Düsendetails">
    <strong>{detail.label}</strong>
    <span><a href={detail.product.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>{detail.product.name} · Herstellerseite ↗</a></span>
    <span>{detail.orifice}</span><span>{detail.pressure}</span>
    <small>Nicht unabhängig geprüft. Düse laut Hersteller: maximal 20 bar, Arbeitsbereich 12–20 bar. <a href="https://www.mrs-shop.com/whisper-powerpump-ultimate-22-bar.html" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>Pumpe laut Hersteller: maximal 22 bar</a>, empfohlen 20 bar. Die Beobachtung überschreitet beide Herstellergrenzen; keine Betriebsfreigabe. Manometer / Messaufbau prüfen.</small>
    <small>Modell: 3 × 3 Düsen. Die Druckbeobachtung betrifft 12 Düsen.</small>
    <small>Vernickelter Messingkörper, Edelstahlkopf mit Keramikeinsatz. T-Steckverteiler für 6 / 4 mm Schlauch; eingebaute Anschlussvariante unbestätigt.</small>
    <small>Hersteller-Referenz je Anti-Tropf-Düse: 30 ml/min bei 15–20 bar. Kein belegter Durchflusswert für 25 bar.</small>
    <small>Tropfengröße nicht gemessen. Die Düsenöffnung ist kein Tropfendurchmesser; für diesen Düsentyp ist hier keine gemessene Tropfenverteilung belegt.</small>
    <button type="button" onClick={onClose} aria-label="Düsendetails schließen">×</button>
  </aside>;
}
// Respect visibility and the first opaque surface: a nozzle behind a closed wall
// must not become an interactive, see-through target. The cut window is transparent.
export function findVisibleNozzleIntersection(intersections) {
  for (const hit of intersections) {
    let visible = true, object = hit.object;
    while (object) { if (!object.visible) visible = false; object = object.parent; }
    if (!visible || !hit.object.isMesh) continue;
    const mats = Array.isArray(hit.object.material) ? hit.object.material : [hit.object.material];
    if (mats.every(mat => mat.transparent && mat.opacity < .3)) continue;
    return hit.object.userData.nozzleId ? hit : null;
  }
  return null;
}
// CAD reference envelopes are intentionally translucent. Every visible CAD
// surface selects the whole measurement assembly, never an individual part.
// Every other opaque surface occludes them: a click cannot pass through a
// closed chamber or through the assembled housing to an internal component.
export function findVisibleMeasurementIntersection(intersections) {
  for (const hit of intersections) {
    let visible = true, object = hit.object;
    while (object) { if (!object.visible) visible = false; object = object.parent; }
    if (!visible || !hit.object.isMesh) continue;
    if (hit.object.userData.cadLabel && measurementCadNames.has(hit.object.name)) return hit;
    const mats = Array.isArray(hit.object.material) ? hit.object.material : [hit.object.material];
    if (mats.every(mat => mat.transparent && mat.opacity < .3)) continue;
    return null;
  }
  return null;
}
export function shouldShowSceneMarker({ cadStatus, selected, labels, index, id }) {
  return Boolean(labels && !(cadStatus === "ready" && measurementPartIds.includes(selected)) && (index < 13 || selected === id));
}
export function findVisibleSceneIntersection(intersections) {
  for (const hit of intersections) {
    let visible = true, object = hit.object;
    while (object) { if (!object.visible) visible = false; object = object.parent; }
    if (!visible || !hit.object.isMesh) continue;
    const mats = Array.isArray(hit.object.material) ? hit.object.material : [hit.object.material];
    if (mats.every(mat => mat.transparent && mat.opacity < .3)) continue;
    return findScenePart(hit.object) ? hit : null;
  }
  return null;
}
export function findVisibleControllerPort(intersections) {
  for (const hit of intersections) {
    let visible = true, ref = "", object = hit.object;
    while (object) { if (!object.visible) visible = false; if (object.userData?.controllerPort) ref = object.userData.controllerPort; object = object.parent; }
    if (!visible || !hit.object.isMesh) continue;
    const mats = Array.isArray(hit.object.material) ? hit.object.material : [hit.object.material];
    if (mats.every(mat => mat.transparent && mat.opacity < .3)) continue;
    return visibleControllerPorts.some(port => port.ref === ref) ? ref : "";
  }
  return "";
}
export function revealGreenhouseScene(element, reducedMotion) {
  if (element && typeof element.scrollIntoView === "function") element.scrollIntoView({ block: "start", behavior: reducedMotion ? "auto" : "smooth" });
}
export function setSceneDrawingBufferSize(renderer, width, height) {
  renderer.setDrawingBufferSize(width, height, renderer.getPixelRatio());
}
export function sceneZoomRadius(radius, factor, nozzleDetail = false) {
  // Keep a nozzle close-up in the gap between channels; every input shares the
  // same limits, so zooming in can never jump backwards into the next chamber.
  return clamp(radius * factor, .35, nozzleDetail ? 1.3 : 36);
}
export function sceneOrbitPhi(phi, delta, selected) {
  return clamp(phi + delta, .18, selected === "controller" ? 2.3 : Math.PI / 2 - .03);
}
// Drawing-space camera proximity, not a physical sensor or measured distance.
// Separate thresholds avoid flicker around a single zoom boundary.
export const chamberCutawayDistance = Object.freeze({ enter: 1.25, leave: 1.8 });
export function nextCameraCutaway(distance, wasOpen = false) {
  return Number.isFinite(distance) && distance >= 0
    && distance < (wasOpen ? chamberCutawayDistance.leave : chamberCutawayDistance.enter);
}
const measurementCadNames = new Set([
  "PRINT_01_Hauptkoerper_PartStudio1_8_Originalkonus_4_blinde_Deckelbohrungen_part",
  "PRINT_02_Deckel_Konus_O80max_4_Stifte_unten_4_Haubenstifte_Lager_Servo_part",
  "REFERENCE_SENSOR_SHAFT_D12_part",
  "PRINT_04_Messrotor_Ansaugung_unten_part",
  "PURCHASE_ADEEPT_AD002_SERVO_ASIN_B09G6KS29F_part",
  "PRINT_05_Trockenrotor_Ansaugung_oben_part",
  "PURCHASE_A2212_SHAFT_D3_175_part",
  "PRINT_07_Servicehaube_Seitenwand_Motorblister_ohne_Aussen_Lagerstege_part",
  "PRINT_08_AD002_Aufsteck_Klemmteil_ohne_Schrauben_part",
  "PURCHASE_A2212_ROTATING_BELL_D30_part",
  "PURCHASE_T2_5_120MM_48T_COMMON_BELT_part",
  "PRINT_SLIDER_P1_MESSEN_part",
  "PURCHASE_T2_5_15T_PULLEY_MOTOR_part",
  "PRINT_SERVO_CRANK_P1_MESSEN_part",
  "PURCHASE_T2_5_15T_PULLEY_DRY_part",
  "PURCHASE_SHAFT_MEASURE_3MM_part",
  "PURCHASE_T2_5_15T_PULLEY_MEASURE_part",
  "PURCHASE_SHAFT_DRY_3MM_part",
  "PRINT_09_Oberer_Servicedeckel_4_Zapfen_unten_Topansaugung_zwei_R2_2RS_Toplagertaschen_part",
  "PURCHASE_R2_2RS_3_175x9_525x3_967_BEARING_5_part",
  "PURCHASE_R2_2RS_3_175x9_525x3_967_BEARING_3_part",
  "PURCHASE_R2_2RS_3_175x9_525x3_967_BEARING_4_part",
  "PURCHASE_R2_2RS_3_175x9_525x3_967_BEARING_6_part",
  "PURCHASE_R2_2RS_3_175x9_525x3_967_BEARING_1_part",
  "PURCHASE_R2_2RS_3_175x9_525x3_967_BEARING_2_part",
  "REFERENCE_REV25_BLDC_PCB_ENVELOPE_28x46_1x1_6_part",
  "REFERENCE_REV25_C204_NOMINAL_LIBRARY_ENVELOPE_part",
  "REFERENCE_REV25_J15_NOMINAL_LIBRARY_ENVELOPE_part"
]);

// This adapter retains the CAD vertices and common assembly coordinates. Only the
// presentation's millimetre scale, up axis and documented mount-point alignment change.
export function createMeasurementCad(THREE, asset) {
  const vector = value => Array.isArray(value) && value.length === 3 && value.every(Number.isFinite);
  if (!asset || asset.schemaVersion !== 1 || asset.units !== "mm" || asset.upAxis !== "Z" || !vector(asset.mountPoint) || !Array.isArray(asset.components) || asset.components.length !== measurementCadNames.size || asset.source?.revision !== 4 || asset.source?.file !== "P1_Messen_REV4.step" || asset.source?.sha256 !== "8e49783d40a1f041712ae268e8e1fd77ccce844dbcd9ec63e67c8e36c0bfb46e" || asset.source?.pose !== "P1 / Messen") throw new Error("Unsupported measurement CAD manifest");
  const names = new Set();
  asset.components.forEach(component => {
    if (!component || typeof component.name !== "string" || !measurementCadNames.has(component.name) || names.has(component.name) || !["printed", "purchased", "reference"].includes(component.kind) || !vector(component.explode) || !Array.isArray(component.positions) || component.positions.length < 9 || component.positions.length % 3 || !component.positions.every(Number.isFinite) || !Array.isArray(component.indices) || component.indices.length < 3 || component.indices.length % 3 || !component.indices.every(index => Number.isInteger(index) && index >= 0 && index < component.positions.length / 3)) throw new Error("Invalid measurement CAD component");
    names.add(component.name);
  });
  const root = new THREE.Group(), coordinates = new THREE.Group();
  root.name = "measurement-rev4-cad"; root.rotation.x = -Math.PI / 2; root.scale.setScalar(.0075);
  coordinates.position.set(...asset.mountPoint.map(value => -value)); root.add(coordinates);
  const components = asset.components.map(component => {
    const geometry = new THREE.BufferGeometry(); geometry.setAttribute("position", new THREE.Float32BufferAttribute(component.positions, 3)); geometry.setIndex(component.indices); geometry.computeVertexNormals();
    const material = new THREE.MeshStandardMaterial({ color: component.color || "#b6c3b7", roughness: component.kind === "printed" ? .74 : .46, metalness: component.kind === "purchased" ? .3 : .04, transparent: component.kind === "reference", opacity: component.kind === "reference" ? .28 : 1, depthWrite: component.kind !== "reference", side: THREE.DoubleSide });
    const object = new THREE.Mesh(geometry, material); object.name = component.name; object.castShadow = component.kind !== "reference"; object.receiveShadow = true;
    object.userData.cadLabel = component.label || component.name; object.userData.cadKind = component.kind; coordinates.add(object);
    return { name: component.name, label: component.label || component.name, kind: component.kind, object, offset: new THREE.Vector3(...component.explode) };
  });
  let disposed = false;
  return {
    root, components, source: asset.source || {},
    setExploded(value) { components.forEach(component => component.object.position.copy(component.offset).multiplyScalar(value ? 1 : 0)); root.updateMatrixWorld(true); },
    dispose() { if (disposed) return; disposed = true; components.forEach(({ object }) => { object.geometry.dispose(); object.material.dispose(); }); root.clear(); }
  };
}

export async function loadMeasurementCad(THREE, url, { signal, fetcher = fetch }) {
  const requireActive = () => { if (signal.aborted) throw new DOMException("Measurement CAD request aborted", "AbortError"); };
  requireActive();
  const response = await fetcher(url, { signal, credentials: "omit" });
  requireActive();
  if (!response.ok) throw new Error("Measurement CAD unavailable");
  const asset = await response.json();
  requireActive();
  return createMeasurementCad(THREE, asset);
}

// Idealized rectangular taper only, not a calibrated real vessel or an exact
// sloping-floor capacity calculation. Nominal litres label the user's tank;
// this normalized integral places its illustrative fraction above 2/3 height.
export function tankNormalizedFillHeight(fraction) {
  if (!Number.isFinite(fraction) || fraction < 0 || fraction > 1) throw new Error("Invalid normalized tank fraction");
  const volume = height => 4.35 * 2 * height + (4.35 * .5 + 2 * .5) * height * height / 2 + .25 * height * height * height / 3;
  if (fraction === 0 || fraction === 1) return fraction;
  let low = 0, high = 1;
  for (let i = 0; i < 48; i += 1) { const mid = (low + high) / 2; if (volume(mid) < fraction * volume(1)) low = mid; else high = mid; }
  return (low + high) / 2;
}

// Presentation-only surface grain. Shared tiny textures, no displacement,
// measurements, downloaded textures or changes to CAD/plant geometry.
export function createGreenhouseSurfaceTextures(THREE) {
  const size = 64, textures = [];
  const grain = (seed, repeat, amplitude) => {
    const pixels = new Uint8Array(size * size * 4);
    for (let i = 0; i < size * size; i += 1) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      const value = 128 + Math.round(((seed >>> 24) / 255 - .5) * amplitude);
      pixels.set([value, value, value, 255], i * 4);
    }
    const texture = new THREE.DataTexture(pixels, size, size, THREE.RGBAFormat);
    texture.name = "greenhouse-shared-surface-grain";
    texture.colorSpace = THREE.NoColorSpace;
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(repeat, repeat);
    texture.minFilter = THREE.LinearMipmapLinearFilter; texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true; texture.needsUpdate = true; textures.push(texture);
    return texture;
  };
  const polymer = grain(0x46454544, 18, 38), rubber = grain(0x43484141, 10, 54);
  // Two-by-two seamless slate tiles. Broad low-contrast stone variation is
  // visible at the initial camera distance; the packed R/G channels provide
  // shallow grout/cleft relief and roughness without additional geometry.
  // This is presentation scenery, never a measured floor or installation.
  const floorSize = 512, tileSize = floorSize / 2;
  const albedoPixels = new Uint8Array(floorSize * floorSize * 4);
  const surfacePixels = new Uint8Array(floorSize * floorSize * 4);
  const smooth = value => { const t = clamp(value, 0, 1); return t * t * (3 - 2 * t); };
  const tileShades = [-2.2, 3.1, 1.2, -1.1];
  const byte = value => Math.round(clamp(value, 0, 255));
  for (let y = 0; y < floorSize; y += 1) {
    for (let x = 0; x < floorSize; x += 1) {
      const u = (x + .5) / floorSize, v = (y + .5) / floorSize;
      const px = (x + .5) % tileSize, py = (y + .5) % tileSize;
      const edge = Math.min(px, py, tileSize - px, tileSize - py);
      const stone = smooth((edge - .8) / 2.0);
      const tile = Math.floor(x / tileSize) + Math.floor(y / tileSize) * 2;
      const broad = Math.sin(2 * Math.PI * (3 * u + 2 * v)) * Math.cos(2 * Math.PI * (u - 3 * v));
      const cleft = Math.sin(2 * Math.PI * (13 * u + 7 * v) + 2.3 * Math.sin(2 * Math.PI * 3 * v) + 1.4 * broad);
      const fine = Math.sin(2 * Math.PI * (67 * u + 41 * v)) * Math.cos(2 * Math.PI * (29 * u - 53 * v));
      const variation = tileShades[tile] + 7 * broad + 4 * cleft + 2.5 * fine;
      const offset = (y * floorSize + x) * 4;
      const mix = (joint, slab) => byte(joint + (slab - joint) * stone);
      albedoPixels.set([mix(34, 51 + variation), mix(44, 70 + variation), mix(36, 53 + variation), 255], offset);
      surfacePixels.set([mix(30, 195 + 7 * broad + 4 * cleft + 2 * fine), mix(236, 140 + 17 * broad + 9 * cleft), 0, 255], offset);
    }
  }
  const floorTexture = (pixels, colorSpace, name) => {
    const texture = new THREE.DataTexture(pixels, floorSize, floorSize, THREE.RGBAFormat);
    texture.name = name; texture.colorSpace = colorSpace;
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    // The unchanged 13.5 × 16 drawing-unit slab gets square 1.5-unit tiles.
    texture.repeat.set(13.5 / 3, 16 / 3);
    texture.minFilter = THREE.LinearMipmapLinearFilter; texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true; texture.needsUpdate = true; textures.push(texture);
    return texture;
  };
  const floorAlbedo = floorTexture(albedoPixels, THREE.SRGBColorSpace, "greenhouse-slate-albedo");
  const floorSurface = floorTexture(surfacePixels, THREE.NoColorSpace, "greenhouse-slate-height-roughness");
  let disposed = false;
  return { polymer, rubber, floorAlbedo, floorSurface, dispose() { if (disposed) return; disposed = true; textures.forEach(texture => texture.dispose()); } };
}

// A bounded reflection environment, not scenery or an external HDR. Linear
// half-float radiance preserves soft bright highlights without adding lights.
export function createGreenhouseSkyTexture(THREE) {
  const width = 256, height = 128, pixels = new Uint16Array(width * height * 4);
  const ground = new THREE.Color(0x424c3c), horizon = new THREE.Color(0xdbe1d5), zenith = new THREE.Color(0x9cb4c3);
  const sun = new THREE.Vector3(-12, 14.2, 10).normalize(), color = new THREE.Color();
  for (let y = 0; y < height; y += 1) {
    const latitude = ((y + .5) / height - .5) * Math.PI, altitude = Math.sin(latitude), radius = Math.cos(latitude);
    for (let x = 0; x < width; x += 1) {
      const longitude = ((x + .5) / width - .5) * Math.PI * 2;
      const alignment = radius * Math.cos(longitude) * sun.x + altitude * sun.y + radius * Math.sin(longitude) * sun.z;
      if (altitude < 0) color.copy(horizon).lerp(ground, Math.min(1, -altitude * 2.7));
      else color.copy(horizon).lerp(zenith, Math.pow(altitude, .55));
      const glow = 4 * Math.pow(Math.max(0, alignment), 48);
      color.r += glow; color.g += glow * .79; color.b += glow * .52;
      const offset = (y * width + x) * 4;
      pixels[offset] = THREE.DataUtils.toHalfFloat(color.r);
      pixels[offset + 1] = THREE.DataUtils.toHalfFloat(color.g);
      pixels[offset + 2] = THREE.DataUtils.toHalfFloat(color.b);
      pixels[offset + 3] = THREE.DataUtils.toHalfFloat(1);
    }
  }
  const texture = new THREE.DataTexture(pixels, width, height, THREE.RGBAFormat, THREE.HalfFloatType);
  texture.name = "greenhouse-procedural-daylight";
  texture.mapping = THREE.EquirectangularReflectionMapping; texture.colorSpace = THREE.LinearSRGBColorSpace;
  texture.minFilter = texture.magFilter = THREE.LinearFilter; texture.needsUpdate = true;
  return texture;
}

export function createGreenhouseEnvironment(THREE, renderer) {
  // Low-capability renderers keep the direct lights. No render-loop polling or
  // second network request is introduced if environment generation is skipped.
  if (!(renderer.capabilities?.maxTextureSize >= 336) || !renderer.extensions?.has("EXT_color_buffer_float")) {
    return { texture: null, dispose() {} };
  }
  let input = null, generator = null, target = null, disposed = false;
  const previousTarget = renderer.getRenderTarget(), previousFace = renderer.getActiveCubeFace(), previousMip = renderer.getActiveMipmapLevel();
  const previousAutoClear = renderer.autoClear, previousXR = renderer.xr.enabled;
  const usedTargets = new Set(), releasedTargets = new WeakSet();
  const releaseTarget = value => {
    if (!value || value === previousTarget || releasedTargets.has(value)) return;
    releasedTargets.add(value); value.dispose();
  };
  // Observe only this synchronous preprocessing operation via the renderer's
  // public API. If a driver throws midway, every touched render target remains
  // owned and releasable; no renderer methods are replaced globally.
  const preprocessingRenderer = new Proxy(renderer, { get(object, key) {
    if (key === "setRenderTarget") return (value, ...args) => {
      if (value && value !== previousTarget && !usedTargets.has(value)) {
        usedTargets.add(value);
        value.addEventListener("dispose", () => releasedTargets.add(value));
      }
      return object.setRenderTarget(value, ...args);
    };
    const value = Reflect.get(object, key, object);
    return typeof value === "function" ? value.bind(object) : value;
  } });
  try {
    input = createGreenhouseSkyTexture(THREE);
    generator = new THREE.PMREMGenerator(preprocessingRenderer);
    // Cold-start must let PMREM allocate its own LOD/blur targets. The optional
    // output-target argument does not initialize these on a fresh r170 generator.
    // 256 px input -> 64 px cube faces, one retained 336 x 256 CubeUV texture.
    target = generator.fromEquirectangular(input);
    return { texture: target.texture, dispose() { if (disposed) return; disposed = true; releaseTarget(target); } };
  } catch (_) {
    target = null;
    return { texture: null, dispose() {} };
  } finally {
    try {
      renderer.setRenderTarget(previousTarget, previousFace, previousMip);
      renderer.autoClear = previousAutoClear; renderer.xr.enabled = previousXR;
    } finally {
      input?.dispose(); generator?.dispose();
      usedTargets.forEach(value => { if (value !== target) releaseTarget(value); });
      input = null; generator = null; usedTargets.clear();
    }
  }
}

// Distant procedural garden backdrop: scenery only, never an extra plant,
// measured environment or external download. A shared equirectangular texture
// avoids hundreds of foliage meshes and remains seamless while orbiting.
export function createGreenhouseBackdropTexture(THREE) {
  const width = 512, height = 256, pixels = new Uint8Array(width * height * 4);
  const smooth = t => t * t * (3 - 2 * t);
  const hash = (x, y, z) => {
    let n = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(z, 2147483647);
    n = Math.imul(n ^ (n >>> 13), 1274126177);
    return ((n ^ (n >>> 16)) >>> 0) / 4294967295;
  };
  const noise = (x, y, z) => {
    const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z);
    const sx = smooth(x - ix), sy = smooth(y - iy), sz = smooth(z - iz);
    const lerp = (a, b, t) => a + (b - a) * t;
    const layer = k => lerp(lerp(hash(ix, iy, k), hash(ix + 1, iy, k), sx), lerp(hash(ix, iy + 1, k), hash(ix + 1, iy + 1, k), sx), sy);
    return lerp(layer(iz), layer(iz + 1), sz);
  };
  for (let y = 0; y < height; y += 1) {
    const latitude = ((y + .5) / height - .5) * Math.PI, altitude = Math.sin(latitude), radius = Math.cos(latitude);
    for (let x = 0; x < width; x += 1) {
      const longitude = ((x + .5) / width - .5) * Math.PI * 2;
      const dx = radius * Math.cos(longitude), dz = radius * Math.sin(longitude);
      const grove = noise(dx * 9, altitude * 8 + 13, dz * 9);
      const foliage = .46 * noise(dx * 37, altitude * 26 + 23, dz * 37) + .32 * noise(dx * 83, altitude * 55 + 59, dz * 83) + .22 * noise(dx * 173, altitude * 110 + 97, dz * 173);
      const sunward = Math.max(0, -.65 * dx + .43 * altitude + .63 * dz);
      const light = clamp((foliage - .33) * 2.2, 0, 1), glint = Math.pow(Math.max(0, (foliage - .56) * 3.2), 2) * sunward;
      const haze = Math.pow(sunward, 14) * .3;
      const sky = smooth(clamp((altitude - .3 - grove * .35) * 6, 0, 1));
      const colors = [27 + 68 * light + 102 * glint + 120 * haze, 46 + 64 * light + 91 * glint + 101 * haze, 30 + 34 * light + 43 * glint + 60 * haze];
      const skyColor = [174, 192, 186];
      const offset = (y * width + x) * 4;
      for (let c = 0; c < 3; c += 1) pixels[offset + c] = Math.round(clamp(colors[c] * (1 - sky) + skyColor[c] * sky, 0, 255));
      pixels[offset + 3] = 255;
    }
  }
  const texture = new THREE.DataTexture(pixels, width, height, THREE.RGBAFormat);
  texture.name = "greenhouse-illustrative-distant-garden"; texture.colorSpace = THREE.SRGBColorSpace;
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.minFilter = texture.magFilter = THREE.LinearFilter; texture.needsUpdate = true;
  return texture;
}

export function createGreenhouseLighting(THREE) {
  const hemisphere = new THREE.HemisphereLight(0xfff2df, 0x354238, .75);
  const sun = new THREE.DirectionalLight(0xffd89e, 4.3);
  sun.position.set(-12, 18, 10); sun.target.position.set(0, 3.8, 0); sun.castShadow = true;
  sun.shadow.mapSize.set(1024, 1024);
  sun.shadow.camera.left = -14; sun.shadow.camera.right = 14;
  sun.shadow.camera.top = 14; sun.shadow.camera.bottom = -14;
  sun.shadow.normalBias = .035;
  const fill = new THREE.DirectionalLight(0xb8d0df, .45);
  fill.position.set(10, 13, -8); fill.target.position.set(0, 4, 0);
  return { hemisphere, sun, fill };
}

// All dimensions are drawing coordinates, not a manufacturing model or a parts specification.
export function createGreenhouseModel(THREE) {
  const v = (x, y, z) => new THREE.Vector3(x, y, z);
  const floorOffset = -1.50, floorLevel = -.05 + floorOffset, tankDepthExtension = 1.10;
  const root = new THREE.Group();
  const parts = new Map();
  const materials = new Set();
  const geometries = new Set();
  const surfaceTextures = createGreenhouseSurfaceTextures(THREE);
  let modelDisposed = false;
  const explodedPieces = [];
  const separable = (object, offset) => { explodedPieces.push({ object, base: object.position.clone(), offset: v(...offset) }); return object; };
  const mesh = (geometry, material, parent, position = [0, 0, 0]) => {
    geometries.add(geometry); materials.add(material);
    const object = new THREE.Mesh(geometry, material);
    object.position.set(...position); object.castShadow = !material.transparent; object.receiveShadow = true;
    parent.add(object); return object;
  };
  const material = (color, options = {}) => new THREE.MeshStandardMaterial({ color, roughness: .67, metalness: 0, ...options });
  const m = {
    frame: material(0x304e41, { metalness: .7, roughness: .29 }),
    dark: material(0x1a4036), ivory: material(0xd6e1cb), metal: material(0x6b8e82, { metalness: .65 }),
    brass: material(0xd3ae67, { metalness: .7 }), leaf: material(0x7cbe72, { side: THREE.DoubleSide }),
    leafDark: material(0x3f8b58), root: material(0xd9caa3), water: material(0x4dbfc1, { emissive: 0x18595b, emissiveIntensity: .23 }),
    airA: material(0xe3b26c, { emissive: 0xa86720, emissiveIntensity: .3 }),
    airB: material(0x9faef4, { emissive: 0x454c91, emissiveIntensity: .3 }),
    data: material(0x8cbf85, { emissive: 0x315e32, emissiveIntensity: .3 }),
    screen: material(0x061e1b, { roughness: .25 }), screenLine: material(0xa8dc9a, { emissive: 0x8cce7d, emissiveIntensity: .5 }),
    glass: material(0xe5eeeb, { roughness: .1, metalness: 0, transparent: true, opacity: .08, depthWrite: false, side: THREE.DoubleSide }),
    chamber: material(0x8cbdab, { transparent: true, opacity: .18, depthWrite: false, side: THREE.DoubleSide }),
    token: material(0xd9ffff, { emissive: 0x97e6e8, emissiveIntensity: .8 })
  };
  const box = (size, mat, parent, at) => mesh(new THREE.BoxGeometry(...size), mat, parent, at);
  const cylinder = (radius, height, mat, parent, at, radial = 24) => mesh(new THREE.CylinderGeometry(radius, radius, height, radial), mat, parent, at);
  const tube = (points, radius, mat, parent, straightSegments = false) => {
    const curve = straightSegments ? new THREE.CurvePath() : new THREE.CatmullRomCurve3(points.map(point => v(...point)), false, "centripetal");
    if (straightSegments) points.slice(1).forEach((point, index) => curve.add(new THREE.LineCurve3(v(...points[index]), v(...point))));
    mesh(new THREE.TubeGeometry(curve, Math.max(12, points.length * 9), radius, 7, false), mat, parent);
    return curve;
  };
  const taperedRoot = (points, radius, parent, order) => {
    const curve = new THREE.CatmullRomCurve3(points, false, "centripetal");
    const segments = order === 0 ? 16 : 9, sides = order === 2 ? 4 : 5;
    const geometry = new THREE.TubeGeometry(curve, segments, 1, sides, false);
    const vertices = geometry.attributes.position;
    for (let ring = 0; ring <= segments; ring += 1) {
      const center = curve.getPointAt(ring / segments), width = radius * (.13 + .87 * Math.pow(1 - ring / segments, .8));
      for (let side = 0; side <= sides; side += 1) {
        const index = ring * (sides + 1) + side;
        vertices.setXYZ(index, center.x + (vertices.getX(index) - center.x) * width, center.y + (vertices.getY(index) - center.y) * width, center.z + (vertices.getZ(index) - center.z) * width);
      }
    }
    geometry.computeVertexNormals();
    const object = mesh(geometry, m.root, parent); object.userData.rootOrder = order;
    return { object, curve, radius, order };
  };
  const bar = (a, b, radius, mat, parent) => {
    const start = v(...a), end = v(...b), delta = end.clone().sub(start);
    const object = cylinder(radius, delta.length(), mat, parent, start.clone().add(end).multiplyScalar(.5).toArray(), 8);
    object.quaternion.setFromUnitVectors(v(0, 1, 0), delta.normalize()); return object;
  };
  const part = (id, at, anchor = [0, .6, 0]) => {
    const group = new THREE.Group(); group.position.set(...at); group.userData.partId = id;
    const point = new THREE.Object3D(); point.position.set(...anchor); group.add(point);
    root.add(group); parts.set(id, { group, anchor: point, base: group.position.clone(), layer: sceneParts.find(item => item[0] === id)[2] });
    return group;
  };
  const base = box([13.5, .2, 16], material(0xffffff, { map: surfaceTextures.floorAlbedo, roughness: .88, roughnessMap: surfaceTextures.floorSurface, bumpMap: surfaceTextures.floorSurface, bumpScale: .002 }), root, [0, -.15 + floorOffset, 0]);
  base.userData.presentationSurface = "slate-floor";
  // A real three-dimensional shell with removable roof and side glazing.
  const shell = new THREE.Group(), roof = new THREE.Group(), glazing = new THREE.Group(); root.add(shell, roof, glazing);
  const { eaves, ridge } = greenhouseEnvelope;
  // The viewing/front plane (+Z) is deliberately open: no end arch, door hoop
  // or front glazing. This does not change the green Aeroponic support frame.
  [-6, -3, 0, 3].forEach(z => {
    [-5.5, 5.5].forEach(x => {
      const post = bar([x, floorLevel, z], [x, eaves, z], .045, m.frame, shell);
      // Opening the house retains the controller's mechanical mounting post.
      post.userData.openHouseHidden = x < 0 && z !== -3;
    });
    bar([-5.5, eaves, z], [0, ridge, z], .045, m.frame, roof);
    bar([0, ridge, z], [5.5, eaves, z], .045, m.frame, roof);
  });
  [-5.5, 5.5].forEach(x => {
    [0.05 + floorOffset, eaves / 2, eaves].forEach(y => {
      const rail = bar([x, y, -6], [x, y, 6], .035, m.frame, shell);
      rail.userData.openHouseHidden = x < 0;
    });
    const pane = box([.012, eaves - floorOffset - .1, 12], m.glass, glazing, [x, (eaves + floorOffset) / 2, 0]);
    pane.userData.openHouseHidden = x < 0;
  });
  bar([0, ridge, -6], [0, ridge, 6], .055, m.frame, roof);
  [-6].forEach(z => {
    bar([-5.5, eaves, z], [5.5, eaves, z], .04, m.frame, shell);
    box([11, eaves - floorOffset - .1, .012], m.glass, glazing, [0, (eaves + floorOffset) / 2, z]);
  });
  const roofLength = Math.hypot(5.5, ridge - eaves), angle = Math.atan2(ridge - eaves, 5.5);
  [-1, 1].forEach(side => {
    const panel = box([roofLength, .012, 12], m.glass, roof, [side * 2.75, (eaves + ridge) / 2, 0]); panel.rotation.z = -side * angle;
  });
  // Three visible channels, stand, front returns and tank follow the supplied photographs.
  // Five net-pot places per channel are user-confirmed; hidden roots/nozzles
  // and the evenly spaced drawing coordinates remain schematic.
  const photoWhite = material(0xf1f0e7, { roughness: .39, metalness: 0, bumpMap: surfaceTextures.polymer, bumpScale: .00055 });
  const photoBlack = material(0x181c1a, { roughness: .64, metalness: 0, bumpMap: surfaceTextures.rubber, bumpScale: .00085 });
  const pipeGreen = material(0x205b42, { roughness: .4, metalness: 0, bumpMap: surfaceTextures.polymer, bumpScale: .0003 });
  const pipeJoint = material(0x426957, { roughness: .57, metalness: 0 });
  const photoSilver = material(0xc6cbca, { roughness: .28, metalness: .88 });
  const cutWindowMaterial = material(0xd5e7dc, { transparent: true, opacity: .13, depthWrite: false, side: THREE.DoubleSide });
  materials.add(cutWindowMaterial);
  const chambersPart = part("chambers", [0, 0, -.2], [-1.95, 2.7, 2.6]);
  const netpotsPart = part("netpots", [0, 0, -.2], [-1.5, 3.25, 2.15]);
  const rootsPart = part("roots", [0, 0, -.2], [0, 4.35, -.2]);
  const bed = new THREE.Group(), plants = new THREE.Group(), roots = new THREE.Group(); chambersPart.add(bed); rootsPart.add(plants, roots);
  const photoChannels = [], netPotAssemblies = [], rootSystems = [], basilPlants = [], channelCenters = [-1.5, 0, 1.5], plantStations = [-2.25, -1.15, -.05, 1.05, 2.15];
  // Three shared detailed leaf surfaces, not separate meshes for each vein.
  // Existing opposite-pair positions and leaf envelope remain unchanged.
  const basilAssets = createBasilPlantAssets(THREE, { material });
  const addLeafPair = (plant, origin, azimuth, length, branch = plant.branches[0]) => {
    const pair = { origin: v(...origin), azimuth, leaves: [], petioles: [], branch };
    pair.branchAnchor = basilBranchAnchorAtHeight(branch.curve, origin[1]);
    [0, 1].forEach(side => {
      const angle = azimuth + side * Math.PI, direction = v(Math.sin(angle), 0, Math.cos(angle));
      const start = pair.origin.clone().addScaledVector(direction, .08); start.y += .025;
      pair.petioles.push(bar(pair.branchAnchor.toArray(), start.toArray(), .009, m.leafDark, plant.group));
      const variant = (plant.leafPairs.length + side) % 3;
      const leaf = mesh(basilAssets.leafGeometries[variant], basilAssets.leafMaterials[variant], plant.group, start.toArray());
      leaf.name = "basil-opposite-leaf"; leaf.rotation.set(-.17 + side * .04, angle, 0, "YXZ"); leaf.scale.setScalar(length);
      pair.leaves.push(leaf);
    });
    plant.leafPairs.push(pair);
  };
  // Keep the front support frame behind the tank's outer rim, not through its
  // walls or solution. The channel overhang is schematic, not a measured span.
  const supportFrame = new THREE.Group(); supportFrame.name = "green-support-frame-outside-tank"; bed.add(supportFrame);
  const supportStations = [-2.95, 2.4];
  [-2.2, 2.2].forEach(x => supportStations.forEach(z => {
    bar([x, floorLevel + .1, z], [x, 3.5, z], .065, pipeGreen, supportFrame);
    [2.0, .4].forEach(y => cylinder(.1, .3, pipeJoint, supportFrame, [x, y, z], 16));
    cylinder(.087, .13, photoBlack, supportFrame, [x, floorLevel + .065, z], 12);
  }));
  supportStations.forEach(z => [2.0, .4].forEach(y => bar([-2.2, y, z], [2.2, y, z], .065, pipeGreen, supportFrame)));
  [-2.2, 2.2].forEach(x => [1.98, .4].forEach(y => bar([x, y, supportStations[0]], [x, y, supportStations[1]], .065, pipeGreen, supportFrame)));
  channelCenters.forEach((x, channelIndex) => {
    const channel = new THREE.Group(); channel.name = `photo-channel-${channelIndex + 1}`; bed.add(channel);
    const bottom = box([.96, .07, 6.4], photoWhite, channel, [x, 2.08, 0]);
    // A bounded viewing window replaces only the middle of the camera-facing wall.
    // Its opaque perimeter, opposite wall, end caps, floor and perforated lid stay assembled.
    const leftWall = new THREE.Group(); channel.add(leftWall);
    const wallFrame = [
      box([.06, .14, 6.4], photoWhite, leftWall, [x - .45, 2.155, 0]),
      box([.06, .14, 6.4], photoWhite, leftWall, [x - .45, 2.925, 0]),
      box([.06, .63, .48], photoWhite, leftWall, [x - .45, 2.54, -2.96]),
      box([.06, .63, .48], photoWhite, leftWall, [x - .45, 2.54, 2.96])
    ];
    const viewWindow = box([.06, .63, 5.44], photoWhite, leftWall, [x - .45, 2.54, 0]);
    viewWindow.name = `illustrative-section-window-${channelIndex + 1}`;
    const rightWall = box([.06, .91, 6.4], photoWhite, channel, [x + .45, 2.54, 0]);
    const front = box([.97, .97, .085], photoWhite, channel, [x, 2.54, 3.24]);
    const back = box([.97, .97, .085], photoWhite, channel, [x, 2.54, -3.24]);
    const lidShape = new THREE.Shape();
    lidShape.moveTo(-.485, -3.24); lidShape.lineTo(.485, -3.24); lidShape.lineTo(.485, 3.24); lidShape.lineTo(-.485, 3.24); lidShape.closePath();
    plantStations.forEach(z => { const opening = new THREE.Path(); opening.absarc(0, -z, .27, 0, Math.PI * 2, true); lidShape.holes.push(opening); });
    const lidGeometry = new THREE.ExtrudeGeometry(lidShape, { depth: .065, bevelEnabled: false, curveSegments: 24 });
    lidGeometry.translate(0, 0, -.0325); lidGeometry.rotateX(-Math.PI / 2);
    const lid = mesh(lidGeometry, photoWhite, channel, [x, 3.03, 0]);
    lid.name = `closed-channel-lid-with-net-pot-openings-${channelIndex + 1}`;
    photoChannels.push({ group: channel, bottom, front, back, lid, lidShape, leftWall, rightWall, viewWindow, wallFrame });
    plantStations.forEach((z, plantIndex) => {
      // Retain the front-middle measurement opening and all its CAD/cable anchors.
      const measurementStation = channelIndex === 1 && plantIndex === plantStations.length - 1;
      const pot = new THREE.Group(); pot.name = `net-pot-${channelIndex + 1}-${plantIndex + 1}`; pot.position.set(x, 0, z); netpotsPart.add(pot);
      if (!measurementStation) cylinder(.32, .065, photoBlack, pot, [0, 3.095, 0], 32);
      const lip = mesh(new THREE.TorusGeometry(.28, .025, 8, 28), photoBlack, pot, [0, 3.13, 0]); lip.rotation.x = Math.PI / 2;
      cylinder(.19, .23, photoBlack, pot, [0, 2.94, 0], 16);
      netPotAssemblies.push({ group: pot, channelIndex, plantIndex, measurementStation });
      if (measurementStation) return;
      const basil = { channelIndex, plantIndex, group: new THREE.Group(), leafPairs: [] };
      basil.group.name = `basil-${channelIndex + 1}-${plantIndex + 1}`; basil.group.position.set(x, 3.12, z); plants.add(basil.group);
      const stemHeight = 1.55 + (plantIndex % 2) * .12, azimuth = channelIndex * .41 + plantIndex * .67;
      basil.branches = [createBasilBranch(THREE, { mesh, material: basilAssets.stemMaterial, parent: basil.group, start: [0, 0, 0], end: [.035, stemHeight, -.05], radius: .022, bend: [.022, 0, -.012] })];
      [.32, .46, .60, .73, .86, .97].forEach((fraction, index) => addLeafPair(basil, [.035 * fraction, stemHeight * fraction, -.05 * fraction], azimuth + index * Math.PI / 2, index === 5 ? .22 : .5 - index * .035));
      [-1, 1].forEach(side => {
        const tip = [side * .24, stemHeight * .79, side * .1];
        const start = basilBranchAnchorAtHeight(basil.branches[0].curve, stemHeight * .4);
        const branch = createBasilBranch(THREE, { mesh, material: basilAssets.stemMaterial, parent: basil.group, start: start.toArray(), end: tip, radius: .012, bend: [side * .025, 0, -side * .012] });
        basil.branches.push(branch);
        [.65, 1].forEach(fraction => addLeafPair(basil, [tip[0] * fraction, stemHeight * (.4 + .39 * fraction), tip[2] * fraction], azimuth + side * .7 + fraction, fraction < 1 ? .32 : .23, branch));
      });
      basilPlants.push(basil);
      // Deterministic illustrative root architecture: thick axes, shorter lateral
      // branches and fine tips. No root is generated in the reserved measurement pot.
      let seed = 139 + channelIndex * 73 + plantIndex * 31;
      const random = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
      const bounded = point => v(clamp(point.x, x - .35, x + .35), clamp(point.y, 2.17, 2.87), clamp(point.z, z - .42, z + .42));
      const system = { channelIndex, plantIndex, station: v(x, 2.86, z), branches: [] };
      for (let axis = 0; axis < 3; axis += 1) {
        const start = v(x + (random() - .5) * .13, 2.86, z + (random() - .5) * .12);
        const end = bounded(v(x + (random() - .5) * .43, 2.18 + random() * .17, z + (random() - .5) * .5));
        const mid = bounded(start.clone().lerp(end, .5).add(v((random() - .5) * .12, 0, (random() - .5) * .12)));
        const main = taperedRoot([start, mid, end], .014 + random() * .004, roots, 0); system.branches.push(main);
        for (let side = 0; side < 2; side += 1) {
          const origin = main.curve.getPoint(.23 + side * .28 + random() * .1), sign = side ? 1 : -1;
          const tip = bounded(origin.clone().add(v(sign * (.09 + random() * .16), -.11 - random() * .16, (random() - .5) * .3)));
          const lateral = taperedRoot([origin, bounded(origin.clone().lerp(tip, .5).add(v(0, .035, sign * .035))), tip], .006 + random() * .002, roots, 1); system.branches.push(lateral);
          for (let fine = 0; fine < 2; fine += 1) {
            const at = lateral.curve.getPoint(.3 + fine * .25), fineTip = bounded(at.clone().add(v((random() - .5) * .14, -.06 - random() * .07, (random() - .5) * .16)));
            system.branches.push(taperedRoot([at, bounded(at.clone().lerp(fineTip, .55).add(v(.018 * sign, 0, .014))), fineTip], .0025 + random() * .001, roots, 2));
          }
        }
      }
      rootSystems.push(system);
    });
    // The user-confirmed common return now connects all three front drains to
    // UVC before the tank. The full connected pipes are built with paths.water.
    cylinder(.16, .10, photoWhite, bed, [x, 2.08, 3.00], 24);
    cylinder(.15, .07, photoWhite, bed, [x, 1.96, 3.11], 24);
    const branch = cylinder(.09, .3, photoWhite, bed, [x + .16, 2.02, 2.98], 16); branch.rotation.z = Math.PI / 2;
    for (let ring = 0; ring < 4; ring += 1) { const ridge = cylinder(.105, .018, photoWhite, bed, [x + .19 + ring * .04, 2.02, 2.98], 16); ridge.rotation.z = Math.PI / 2; }
  });
  // Keep the upper tank assembly fixed; enlarge the vessel downwards into the
  // lowered floor space without moving the EC/UVC return, plants or chambers.
  const tankLidHeight = 1.10;
  const reservoir = part("reservoir", [0, 0, 3.75], [0, tankLidHeight + .14, .25]);
  const tankWalls = new THREE.Group(); tankWalls.name = "photo-rectangular-tank"; reservoir.add(tankWalls);
  const tankSectionSurfaces = [];
  const fillHeightFraction = tankNormalizedFillHeight(30 / 45), tankLowFloorY = .24 - tankDepthExtension;
  const tankFillLevelY = tankLowFloorY + (.97 - tankLowFloorY) * fillHeightFraction;
  const refillTankEntry = v(1.35, tankLidHeight, 4.45), drainTankEntry = v(-1.1, tankLowFloorY, 4.62);
  const suctionFloorEntry = v(-1.8, tankLowFloorY, 4.50), floatLevel = v(1.7, tankFillLevelY, 4.45);
  const tankCapacity = Object.freeze({ nominalLitres: 45, levelLimitLitres: 30, normalizedVolumeFraction: 30 / 45, normalizedHeightFraction: fillHeightFraction, basis: "idealized-rectangular-taper-not-calibrated-litres", slopingFloorVolumeCalibrated: false, levelTelemetry: false });
  reservoir.userData.capacity = tankCapacity;
  // Illustrative tapered vessel and sloping floor, not fabrication dimensions or
  // a claim of complete drainage. Two independent outlets pierce its low floor.
  const floorHeight = z => tankLowFloorY + .18 * Math.max(0, (.65 - z) / 1.65);
  const floorThickness = .06, tankFloor = new THREE.Group(); tankFloor.name = "sloping-tank-floor-with-low-collection-zone"; tankWalls.add(tankFloor);
  const floorPanel = (fromZ, toZ, openings) => {
    const shape = new THREE.Shape(); shape.moveTo(-2.175, -fromZ); shape.lineTo(2.175, -fromZ);
    shape.lineTo(2.175, -toZ); shape.lineTo(-2.175, -toZ); shape.closePath();
    openings.forEach(({ point, radius }) => { const hole = new THREE.Path(); hole.absarc(point.x, -(point.z - reservoir.position.z), radius, 0, Math.PI * 2, true); shape.holes.push(hole); });
    const geometry = new THREE.ExtrudeGeometry(shape, { depth: floorThickness, bevelEnabled: false, curveSegments: 32 }); geometry.rotateX(-Math.PI / 2);
    const positions = geometry.attributes.position;
    for (let i = 0; i < positions.count; i += 1) positions.setY(i, positions.getY(i) + floorHeight(positions.getZ(i)) - floorThickness);
    geometry.computeVertexNormals(); return mesh(geometry, photoBlack, tankFloor);
  };
  floorPanel(-1, .65, []).name = "tank-floor-slope";
  floorPanel(.65, 1, [{ point: suctionFloorEntry, radius: .065 }, { point: drainTankEntry, radius: .066 }]).name = "tank-low-floor-with-two-outlet-holes";
  const wall = (corners, inset, name) => {
    const geometry = new THREE.BufferGeometry(), points = [...corners, ...corners.map(point => point.map((value, axis) => value + inset[axis]))];
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(points.flat(), 3));
    geometry.setIndex([0, 1, 2, 0, 2, 3, 4, 6, 5, 4, 7, 6, 0, 4, 5, 0, 5, 1, 1, 5, 6, 1, 6, 2, 2, 6, 7, 2, 7, 3, 3, 7, 4, 3, 4, 0]);
    geometry.computeVertexNormals(); const panel = mesh(geometry, photoBlack, tankWalls); panel.name = name; tankSectionSurfaces.push(panel); return panel;
  };
  const tankFrontPanel = wall([[-2.175, .18 - tankDepthExtension, 1], [2.175, .18 - tankDepthExtension, 1], [2.425, .97, 1.25], [-2.425, .97, 1.25]], [0, 0, -.06], "tank-tapered-closed-front");
  wall([[2.175, .36 - tankDepthExtension, -1], [-2.175, .36 - tankDepthExtension, -1], [-2.425, .97, -1.25], [2.425, .97, -1.25]], [0, 0, .06], "tank-tapered-closed-rear");
  [-1, 1].forEach(side => {
    [[-1, .65], [.65, 1]].forEach(([a, b]) => {
      const corners = [[side * 2.175, floorHeight(b) - floorThickness, b], [side * 2.175, floorHeight(a) - floorThickness, a], [side * 2.425, .97, a * 1.25], [side * 2.425, .97, b * 1.25]];
      wall(side === 1 ? corners : corners.reverse(), [-side * .06, 0, 0], "tank-tapered-side");
    });
    [-.98, .98].forEach(z => { const bottom = floorLevel + .002, height = floorHeight(z) - floorThickness - bottom; box([.14, height, .14], photoBlack, tankWalls, [side * 2.1, bottom + height / 2, z]).name = "tank-bottom-clearance-foot"; });
  });
  [-2.36, 2.36].forEach(x => box([.19, .13, 2.57], photoBlack, tankWalls, [x, 1.00, 0]));
  [-1.18, 1.18].forEach(z => box([4.91, .13, .19], photoBlack, tankWalls, [0, 1.00, z]));
  const tankWater = box([4.50, .025, 2.12], material(0x183b35, { roughness: .23 }), tankWalls, [0, tankFillLevelY, 0]);
  const tankWaterMaterial = tankWater.material;
  // Full opaque lid with a real opening around the unchanged common fallpipe.
  // The rim is illustrative; neither fit nor a sterile seal is validated here.
  const fallpipe = { x: 0, z: 3.18, radius: .18 };
  const tankCover = new THREE.Group(); tankCover.position.set(0, tankLidHeight, 0);
  tankCover.name = "closed-tank-cover-around-fallpipe"; reservoir.add(tankCover);
  separable(tankCover, [0, .45, 0]);
  const tankLidShape = new THREE.Shape();
  tankLidShape.moveTo(-2.455, -1.285); tankLidShape.lineTo(2.455, -1.285);
  tankLidShape.lineTo(2.455, 1.285); tankLidShape.lineTo(-2.455, 1.285); tankLidShape.closePath();
  const fallpipeOpening = new THREE.Path(), fallpipeLocalZ = fallpipe.z - reservoir.position.z;
  fallpipeOpening.absarc(fallpipe.x, -fallpipeLocalZ, fallpipe.radius + .01, 0, Math.PI * 2, true);
  tankLidShape.holes.push(fallpipeOpening);
  const refillOpening = new THREE.Path(); refillOpening.absarc(refillTankEntry.x, -(refillTankEntry.z - reservoir.position.z), .055, 0, Math.PI * 2, true); tankLidShape.holes.push(refillOpening);
  const floatStemOpening = new THREE.Path(); floatStemOpening.absarc(floatLevel.x, -(floatLevel.z - reservoir.position.z), .035, 0, Math.PI * 2, true); tankLidShape.holes.push(floatStemOpening);
  // Confirmed sensor locations, illustrative lid penetrations and immersion depth.
  const tankProbeLocations = { temperature: [-1.35, tankLidHeight + .04, 4.25], ecIn: [-.75, tankLidHeight + .04, 4.25] };
  Object.entries(tankProbeLocations).forEach(([id, [x, , z]]) => {
    const opening = new THREE.Path(); opening.absarc(x, -(z - reservoir.position.z), id === "temperature" ? .045 : .085, 0, Math.PI * 2, true); tankLidShape.holes.push(opening);
  });
  const tankLidGeometry = new THREE.ExtrudeGeometry(tankLidShape, { depth: .07, bevelEnabled: false, curveSegments: 48 });
  tankLidGeometry.translate(0, 0, -.035); tankLidGeometry.rotateX(-Math.PI / 2);
  mesh(tankLidGeometry, photoWhite, tankCover).name = "tank-cover-panel";
  const fallpipeRim = mesh(new THREE.TorusGeometry(fallpipe.radius + .015, .015, 12, 48), photoBlack, tankCover, [fallpipe.x, .04, fallpipeLocalZ]);
  fallpipeRim.rotation.x = Math.PI / 2; fallpipeRim.name = "tank-cover-fallpipe-rim";
  // Separate maintenance paths. Their geometry only illustrates connections;
  // none is connected to the spray-loop tokens, controller pins or live states.
  const osmosis = part("osmosis", [3.85, floorLevel - .078, 4.0], [0, 1.9, .4]);
  osmosis.name = "schematic-waterdrop-g5p500";
  osmosis.userData.product = "Waterdrop G5P500";
  const roundedFace = (width, height, radius, depth, mat, parent, at) => {
    const shape = new THREE.Shape(), x = width / 2, y = height / 2, r = radius;
    shape.moveTo(-x + r, -y); shape.lineTo(x - r, -y); shape.quadraticCurveTo(x, -y, x, -y + r);
    shape.lineTo(x, y - r); shape.quadraticCurveTo(x, y, x - r, y); shape.lineTo(-x + r, y);
    shape.quadraticCurveTo(-x, y, -x, y - r); shape.lineTo(-x, -y + r); shape.quadraticCurveTo(-x, -y, -x + r, -y);
    const geometry = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false, curveSegments: 12 }); geometry.translate(0, 0, -depth / 2);
    return mesh(geometry, mat, parent, at);
  };
  // Listing proportions only: narrow upright white body and rounded grey face.
  // No filter internals, certification mark, measured installation size or display.
  const osmosisBody = roundedFace(.66, 1.6, .09, 1.92, photoWhite, osmosis, [0, .88, 0]);
  osmosisBody.name = "osmosis-white-upright-body";
  roundedFace(.60, 1.50, .09, .028, material(0xb8bdba), osmosis, [0, .88, .974]).name = "osmosis-rounded-grey-front";
  const osmosisPorts = {};
  [["rawWater", -.55], ["purifiedWater", -.12], ["concentrate", .30]].forEach(([name, z]) => {
    const port = cylinder(.045, .15, photoWhite, osmosis, [.395, .48, z], 20); port.rotation.z = Math.PI / 2;
    port.name = `schematic-osmosis-${name}-port`; port.userData.portRole = name;
    const ring = mesh(new THREE.TorusGeometry(.034, .008, 8, 20), photoBlack, osmosis, [.475, .48, z]); ring.rotation.y = Math.PI / 2;
    const anchor = new THREE.Object3D(); anchor.position.set(.475, .48, z); osmosis.add(anchor); osmosisPorts[name] = anchor;
  });
  root.updateMatrixWorld(true);
  const rawWater = osmosisPorts.rawWater.getWorldPosition(v(0, 0, 0)), pureWater = osmosisPorts.purifiedWater.getWorldPosition(v(0, 0, 0)), concentrate = osmosisPorts.concentrate.getWorldPosition(v(0, 0, 0));
  const externalRaw = v(5.1, .36 + floorOffset, 2.4), externalConcentrate = v(5.1, .18 + floorOffset, 5.6);
  const passiveClean = material(0x92aaa5), passiveWaste = material(0x827365);
  // Curves are world-coordinate paths under zero-position child groups.
  // Counter the exterior translation once, keeping the exposed port anchors exact.
  const externalPipes = new THREE.Group(); externalPipes.position.copy(osmosis.position).negate(); osmosis.add(externalPipes);
  const rawWaterCurve = tube([externalRaw.toArray(), [4.7, externalRaw.y, 2.4], [4.7, rawWater.y, rawWater.z], rawWater.toArray()], .025, passiveClean, externalPipes, true);
  const concentrateCurve = tube([concentrate.toArray(), [4.85, concentrate.y, concentrate.z], [4.85, externalConcentrate.y, 5.6], externalConcentrate.toArray()], .025, passiveWaste, externalPipes, true);
  [[externalRaw, "external-raw-water-supply"], [externalConcentrate, "external-concentrate-disposal"]].forEach(([point, name]) => {
    const end = mesh(new THREE.TorusGeometry(.045, .012, 8, 20), photoSilver, externalPipes, point.toArray()); end.rotation.y = Math.PI / 2; end.name = name; end.userData.external = true;
  });
  const refill = part("refill", [0, 0, 0], [3.0, 2.3, 5.15]);
  refill.name = "passive-osmosis-to-tank-refill";
  const refillValve = new THREE.Group(); refillValve.position.set(3.0, 1.7, 5.15); refill.add(refillValve);
  refillValve.name = "mrs-24v-normally-closed-refill-valve";
  refillValve.userData = { representation: "deenergized-normally-closed-not-live", product: "M.R.S. 24 V DC NC G1/8", statedPowerW: 12, statedPressureBar: [0, 35], passive: true };
  box([.35, .17, .23], m.brass, refillValve, [0, 0, 0]);
  cylinder(.09, .16, m.brass, refillValve, [0, .12, 0], 20);
  box([.26, .29, .25], photoBlack, refillValve, [0, .30, 0]);
  cylinder(.045, .05, photoSilver, refillValve, [0, .47, 0], 12);
  [-1, 1].forEach(side => { const port = cylinder(.065, .22, m.brass, refillValve, [side * .22, 0, 0], 20); port.rotation.z = Math.PI / 2; });
  const refillPorts = { inlet: v(3.33, 1.7, 5.15), outlet: v(2.67, 1.7, 5.15) };
  const refillSupply = tube([pureWater.toArray(), [4.55, pureWater.y, pureWater.z], [4.55, 1.7, pureWater.z], [4.55, 1.7, 5.15], refillPorts.inlet.toArray()], .026, passiveClean, refill, true);
  const refillOutlet = v(refillTankEntry.x, tankLidHeight - .10, refillTankEntry.z);
  const refillDelivery = tube([refillPorts.outlet.toArray(), [1.35, 1.7, 5.15], [1.35, 1.7, 4.45], refillOutlet.toArray()], .026, passiveClean, refill, true);
  cylinder(.052, .10, photoWhite, refill, refillTankEntry.toArray(), 20).name = "separate-upper-tank-refill-fitting";
  // Analog level limit: it can prevent/shorten a requested timed opening window,
  // never initiate refill or produce a digital level reading. This line is a
  // functional explanation only, not an approved electrical wiring diagram.
  const floatSwitch = new THREE.Group(); floatSwitch.position.copy(floatLevel); refill.add(floatSwitch);
  floatSwitch.name = "schematic-analog-refill-limit-switch";
  floatSwitch.userData = { representation: "illustrative-analog-level-limit-not-live", startsRefill: false, levelTelemetry: false, timedOpeningWindow: true, alreadyFull: "inhibit-opening", targetReached: "early-close", electricalWiringValidated: false };
  const floatStemTop = 1.18 - floatLevel.y;
  cylinder(.022, floatStemTop + .09, photoSilver, floatSwitch, [0, (floatStemTop - .09) / 2, 0], 16).name = "analog-float-stem";
  cylinder(.11, .085, photoWhite, floatSwitch, [0, 0, 0], 24).name = "analog-float-at-illustrated-limit";
  cylinder(.048, .045, photoBlack, floatSwitch, [0, 1.165 - floatLevel.y, 0], 16);
  const floatSignalStart = v(floatLevel.x, 1.1875, floatLevel.z), floatSignalEnd = v(3, 2.195, 5.15);
  const floatSignal = tube([floatSignalStart.toArray(), [1.7, 1.42, 4.45], [2.35, 1.42, 4.45], [2.5, 1.42, 5.42], [3, 2.3, 5.42], [3, 2.3, 5.15], floatSignalEnd.toArray()], .012, material(0xcf9ea8), refill, true);
  refill.children.find(object => object.geometry?.parameters?.path === floatSignal).name = "schematic-early-close-signal-not-wiring";
  const bottomPipeY = floorOffset + .23;
  const drain = part("drain", [0, 0, 0], [-1.1, bottomPipeY + .44, 5.35]);
  drain.name = "separate-manual-tank-water-change-drain";
  const drainValve = new THREE.Group(); drainValve.position.set(-1.1, bottomPipeY, 5.35); drain.add(drainValve);
  drainValve.name = "schematic-manual-drain-shutoff"; drainValve.userData = { passive: true, manual: true, representation: "closed-not-live", product: null };
  const drainBody = cylinder(.085, .32, photoSilver, drainValve, [0, 0, 0], 24); drainBody.rotation.x = Math.PI / 2;
  cylinder(.025, .14, photoSilver, drainValve, [0, .08, 0], 16);
  box([.26, .04, .055], material(0x9c7760), drainValve, [0, .16, 0]).name = "manual-crosswise-closed-handle";
  const drainPorts = { inlet: v(-1.1, bottomPipeY, 5.19), outlet: v(-1.1, bottomPipeY, 5.51) }, drainInside = v(drainTankEntry.x, tankLowFloorY + .01, drainTankEntry.z), externalDrain = v(-2.0, bottomPipeY, 5.8);
  const drainIntake = tube([drainInside.toArray(), [drainTankEntry.x, bottomPipeY, drainTankEntry.z], drainPorts.inlet.toArray()], .045, photoBlack, drain, true);
  const drainDisposal = tube([drainPorts.outlet.toArray(), [-1.1, bottomPipeY, 5.65], externalDrain.toArray()], .045, passiveWaste, drain, true);
  const drainCollar = mesh(new THREE.TorusGeometry(.064, .014, 8, 24), photoSilver, drain, drainTankEntry.toArray()); drainCollar.rotation.x = Math.PI / 2; drainCollar.name = "underside-tank-drain-bulkhead";
  const drainEnd = mesh(new THREE.TorusGeometry(.059, .012, 8, 20), photoSilver, drain, externalDrain.toArray()); drainEnd.rotation.y = Math.PI / 2; drainEnd.name = "external-tank-water-change-disposal"; drainEnd.userData.external = true;
  const waterMaintenance = {
    osmosisBody, osmosisPorts, externalRaw, externalConcentrate, refillValve, refillPorts, refillTankEntry, refillOutlet,
    drainValve, drainPorts, drainTankEntry, drainInside, externalDrain, tankFrontPanel,
    floatSwitch, floatLevel, floatSignal, floatSignalStart, floatSignalEnd,
    edges: [
      { from: "external-raw-water", to: "osmosis-raw-water", kind: "raw-water", curve: rawWaterCurve },
      { from: "osmosis-concentrate", to: "external-concentrate", kind: "concentrate", curve: concentrateCurve },
      { from: "osmosis-purified-water", to: "refill-valve", kind: "refill", curve: refillSupply },
      { from: "refill-valve", to: "tank-upper-inlet", kind: "refill", curve: refillDelivery },
      { from: "tank-lower-drain", to: "manual-drain-valve", kind: "drain", curve: drainIntake },
      { from: "manual-drain-valve", to: "external-water-change", kind: "drain", curve: drainDisposal }
    ]
  };
  // Manufacturer-photo proportions; low placement is schematic. This sieve
  // prefilter stays upstream of the pump, never in the high-pressure ring.
  const microfilter = part("microfilter", [-3.6, .45 + floorOffset, 4.6], [0, .7, 0]);
  microfilter.name = "microfilter-before-pump";
  microfilter.userData.mounting = "schematic-low-upright-intake-prefilter";
  const prefilterExterior = createMrsPrefilterModel(THREE, { mesh, material }); microfilter.add(prefilterExterior.root);
  root.updateMatrixWorld(true);
  const microfilterPorts = { inlet: prefilterExterior.ports.inlet.getWorldPosition(v(0, 0, 0)), outlet: prefilterExterior.ports.outlet.getWorldPosition(v(0, 0, 0)) };
  const filterInletLead = microfilterPorts.inlet.clone().addScaledVector(prefilterExterior.inletDirection, .18);
  const filterOutletLead = microfilterPorts.outlet.clone().addScaledVector(prefilterExterior.outletDirection, .18);
  const tankIntake = v(suctionFloorEntry.x, tankLowFloorY + .01, suctionFloorEntry.z), tankOutlet = v(suctionFloorEntry.x, tankLowFloorY - floorThickness, suctionFloorEntry.z);
  // Separate bottom pickup; neither residual volume nor dry-run safety is proven.
  const bulkhead = mesh(new THREE.TorusGeometry(.065, .012, 8, 24), photoSilver, reservoir, [suctionFloorEntry.x, tankOutlet.y, suctionFloorEntry.z - reservoir.position.z]);
  bulkhead.rotation.x = Math.PI / 2; bulkhead.name = "low-tank-suction-bulkhead";
  const pickupMouth = mesh(new THREE.TorusGeometry(.043, .012, 8, 24), photoBlack, reservoir, [tankIntake.x, tankIntake.y, tankIntake.z - reservoir.position.z]);
  pickupMouth.rotation.x = Math.PI / 2; pickupMouth.name = "submerged-intake-mouth";
  // The lamp is mounted inside the common vertical gravity fallpipe, directly
  // below EC-OUT and the three-return junction. No separate pumped treatment unit.
  const uvc = part("uvc", [fallpipe.x, 1.53, fallpipe.z], [.4, .18, .15]);
  uvc.name = "uvc-in-common-fallpipe";
  const uvcPipe = cylinder(fallpipe.radius, .54, photoWhite, uvc, [0, 0, 0], 32);
  uvcPipe.name = "closed-common-fallpipe";
  [-1, 1].forEach(side => {
    cylinder(.21, .07, photoBlack, uvc, [0, side * .27, 0], 24);
    cylinder(fallpipe.radius, .08, photoWhite, uvc, [0, side * .34, 0], 20);
  });
  const uvcLamp = new THREE.Group(); uvcLamp.name = "schematic-lamp-inside-fallpipe"; uvc.add(uvcLamp);
  cylinder(.045, .39, photoSilver, uvcLamp, [0, 0, 0], 20);
  [-1, 1].forEach(side => cylinder(.055, .04, photoBlack, uvcLamp, [0, side * .205, 0], 16));
  uvcLamp.visible = false;
  // An explicitly selected cutaway reveals only an unlit, schematic lamp body.
  // No UV rays, activation state, power, dose, mounting detail or wiring claim.
  const uvcPorts = { inlet: v(0, 1.91, 3.18), outlet: v(0, 1.15, 3.18) };
  // External diaphragm-pump form only: no invented impeller, membrane or internal section.
  const pump = part("pump", [-3.9, floorLevel + .0125, 2.5], [0, .65, .1]);
  const pumpExterior = new THREE.Group(); pumpExterior.name = "photo-pump-exterior"; pump.add(pumpExterior);
  pumpExterior.scale.setScalar(.5);
  box([1.3, .1, .9], photoBlack, pumpExterior, [-.12, .065, 0]);
  [-.48, .23].forEach(x => [-.32, .32].forEach(z => {
    box([.22, .2, .18], photoBlack, pumpExterior, [x, .16, z]);
    const boltHole = mesh(new THREE.TorusGeometry(.055, .015, 6, 16), material(0x303635), pumpExterior, [x, .122, z]); boltHole.rotation.x = Math.PI / 2;
  }));
  const motor = cylinder(.37, 1.08, photoBlack, pumpExterior, [-.15, .54, 0], 40); motor.rotation.z = Math.PI / 2;
  cylinder(.385, .095, photoBlack, pumpExterior, [-.74, .54, 0], 40).rotation.z = Math.PI / 2;
  const pumpNameplate = createPumpNameplate(THREE, { mesh, material }); pumpExterior.add(pumpNameplate.root);
  const head = new THREE.Group(); head.name = "visible-lobed-pump-head"; pumpExterior.add(head);
  cylinder(.395, .43, photoBlack, head, [.6, .54, 0], 32).rotation.z = Math.PI / 2;
  cylinder(.405, .035, material(0x080d0c), head, [.42, .54, 0], 32).rotation.z = Math.PI / 2;
  for (let i = 0; i < 4; i += 1) {
    const angle = Math.PI / 4 + i * Math.PI / 2, y = .54 + Math.cos(angle) * .29, z = Math.sin(angle) * .29;
    const lobe = mesh(new THREE.SphereGeometry(.19, 16, 10), photoBlack, head, [.83, y, z]); lobe.scale.set(1.45, 1, 1);
    cylinder(.045, .035, photoSilver, head, [1.05, y, z], 6).rotation.z = Math.PI / 2;
    box([.008, .05, .012], photoBlack, head, [1.073, y, z]);
  }
  cylinder(.29, .23, photoBlack, head, [.94, .54, 0], 24).rotation.z = Math.PI / 2;
  const pumpPorts = [];
  const sidePort = cylinder(.15, .21, photoBlack, head, [.67, .49, .43], 20); sidePort.rotation.x = Math.PI / 2;
  const sideRing = mesh(new THREE.TorusGeometry(.102, .035, 8, 24), material(0x292f2e), head, [.67, .49, .55]); pumpPorts.push(sideRing);
  cylinder(.13, .24, photoBlack, head, [1.17, .35, -.08], 20).rotation.z = Math.PI / 2;
  const frontRing = mesh(new THREE.TorusGeometry(.09, .03, 8, 24), material(0x292f2e), head, [1.3, .35, -.08]); frontRing.rotation.y = Math.PI / 2; pumpPorts.push(frontRing);
  tube([[.5, .2, -.2], [.24, .12, .65], [-.45, .1, .91], [-.8, .16, .85]], .035, photoBlack, pumpExterior);
  cylinder(.1, .29, photoBlack, pumpExterior, [-.9, .16, .85], 16).rotation.z = Math.PI / 2;
  cylinder(.105, .035, material(0x953f32), pumpExterior, [-1.055, .16, .85], 16).rotation.z = Math.PI / 2;
  box([.13, .05, .1], photoBlack, pumpExterior, [-.9, .27, .85]);
  const pumpPowerEndpoint = new THREE.Object3D(); pumpPowerEndpoint.position.set(-1.0725, .16, .85); pumpExterior.add(pumpPowerEndpoint);
  root.updateMatrixWorld(true);
  const pumpInlet = pumpPorts[0].getWorldPosition(v(0, 0, 0)), pumpOutlet = pumpPorts[1].getWorldPosition(v(0, 0, 0));
  const nozzle = part("nozzle", [0, 0, -.2], [0, 2.5, 1.8]);
  const nozzleInternals = new THREE.Group(); nozzleInternals.name = "schematic-hidden-nozzles"; nozzle.add(nozzleInternals);
  // Keep the three unmeasured holder positions in gaps between the five pots;
  // ring sockets and the illustrative mist use these same station anchors.
  const nozzleAssemblies = [], nozzleStations = [-1.7, -.6, 1.6];
  const holderMaterial = material(0x919b98, { roughness: .53, metalness: .25 });
  const ringEdges = [], ringNodes = [];
  channelCenters.forEach((x, channelIndex) => {
    // User-identified grey cylinders are three nozzle holders per chamber, not four generic couplers.
    // Their drawn positions and body dimensions are illustrative; 0.2 mm describes only the stated orifice.
    const lineX = x + .32;
    nozzleStations.forEach((z, nozzleIndex) => {
      const detail = nozzleDetails[channelIndex * 3 + nozzleIndex];
      const assembly = { ...createMicroLineNozzleAssembly(THREE, { mesh, material, position: [lineX, 3.17, z], id: detail.id }), id: detail.id, channelIndex, nozzleIndex };
      nozzle.add(assembly.root); nozzleInternals.add(assembly.internals);
      assembly.holder.name = `nozzle-holder-${channelIndex + 1}-${nozzleIndex + 1}`;
      assembly.sprayHead.name = `inward-spray-nozzle-${channelIndex + 1}-${nozzleIndex + 1}`;
      const { ports } = assembly;
      nozzleAssemblies.push(assembly); ringNodes.push({ id: detail.id, ports });
    });
  });
  // User-confirmed closed pressure ring and one T supply. Unseen bends and fitting
  // dimensions are illustrative; this is not the separate gravity return to the tank.
  const tee = { id: "pressure-tee", center: v(-.5, 1.85, 3.2), ports: { left: v(-.68, 1.85, 3.2), right: v(-.32, 1.85, 3.2), feed: v(-.5, 1.85, 3.02) } };
  const teeGroup = new THREE.Group(); teeGroup.name = "pressure-ring-three-port-tee"; nozzle.add(teeGroup);
  bar(tee.ports.left.toArray(), tee.ports.right.toArray(), .065, holderMaterial, teeGroup);
  bar(tee.center.toArray(), tee.ports.feed.toArray(), .065, holderMaterial, teeGroup);
  Object.values(tee.ports).forEach(port => {
    const inward = tee.center.clone().sub(port).normalize(), center = port.clone().addScaledVector(inward, .025);
    const collar = cylinder(.075, .05, photoSilver, teeGroup, center.toArray(), 20); collar.quaternion.setFromUnitVectors(v(0, 1, 0), inward);
  });
  ringNodes.push(tee);
  const connectRing = (from, fromPort, to, toPort, via = []) => {
    const start = from.ports[fromPort], end = to.ports[toPort];
    const curve = tube([start.toArray(), ...via, end.toArray()], .028, photoBlack, nozzle);
    const edge = { from: from.id, to: to.id, fromPort, toPort, start: start.clone(), end: end.clone(), curve };
    ringEdges.push(edge); return edge;
  };
  const a = nozzleAssemblies;
  connectRing(tee, "left", a[2], "positive", [[-.85, 1.85, 3.2], [-1.18, 1.85, 3.4], [-1.18, 2.8, 3.5], [-1.18, 3.17, 2.8], [-1.18, 3.17, 1.95]]);
  connectRing(a[2], "negative", a[1], "positive"); connectRing(a[1], "negative", a[0], "positive");
  connectRing(a[0], "negative", a[3], "negative", [[-1.18, 3.17, -2.55], [-.8, 3.17, -2.8], [-.06, 3.17, -2.8], [.32, 3.17, -2.55]]);
  connectRing(a[3], "positive", a[4], "negative"); connectRing(a[4], "positive", a[5], "negative");
  connectRing(a[5], "positive", a[8], "positive", [[.32, 3.17, 2.55], [.7, 3.17, 2.8], [1.44, 3.17, 2.8], [1.82, 3.17, 2.55]]);
  connectRing(a[8], "negative", a[7], "positive"); connectRing(a[7], "negative", a[6], "positive");
  connectRing(a[6], "negative", tee, "right", [[1.82, 3.17, -2.55], [2.12, 3.17, -2.9], [2.65, 3.17, -2.9], [2.8, 3.17, -2.5], [2.8, 3.17, 2.8], [2.7, 2.8, 3.2], [2.6, 1.85, 3.2], [.1, 1.85, 3.2]]);
  tube([[2.25, 3.16, 2.6], [2.4, 2.3, 3.0], [2.5, 1.15, 3.4], [2.3, .95, 3.65]], .015, photoWhite, nozzle);
  const mistGeometry = new THREE.BufferGeometry(), mistCoordinates = new Float32Array(216 * 3);
  for (let i = 0; i < 216; i += 1) { const n = i % 3, station = Math.floor(i / 3) % 3, angle = i * 2.399; const r = ((i * 17) % 59) / 59 * .085; mistCoordinates[i * 3] = channelCenters[n] + .32 + Math.cos(angle) * r; mistCoordinates[i * 3 + 1] = 2.25 + ((i * 11) % 31) / 31 * .38; mistCoordinates[i * 3 + 2] = nozzleStations[station] + Math.sin(angle) * r; }
  mistGeometry.setAttribute("position", new THREE.BufferAttribute(mistCoordinates, 3)); geometries.add(mistGeometry);
  const mistMaterial = new THREE.PointsMaterial({ color: 0x9edfe1, size: .025, transparent: true, opacity: .72, depthWrite: false }); materials.add(mistMaterial);
  const mist = new THREE.Points(mistGeometry, mistMaterial); nozzle.add(mist); mist.visible = false;
  // User-confirmed location: the chamber occupies one net-pot opening. The middle/front
  // station is only an illustrative choice; the insert's hidden internal geometry is schematic.
  const air = part("air", [0, 3.12, 1.95], [0, .9, 0]);
  air.name = "measurement-insert-in-net-pot";
  const measurementMount = new THREE.Group(), measurementSchematic = new THREE.Group();
  measurementMount.name = "measurement-cad-mount"; measurementSchematic.name = "schematic-measurement-insert";
  air.add(measurementMount, measurementSchematic);
  cylinder(.31, .075, photoBlack, measurementSchematic, [0, -.08, 0], 28);
  const chamberShell = cylinder(.26, .57, m.chamber, measurementSchematic, [0, .16, 0], 28);
  const chamberLid = separable(cylinder(.29, .065, photoBlack, measurementSchematic, [0, .47, 0], 28), [0, .42, 0]);
  chamberLid.name = "schematic-measurement-insert-lid";
  const fan = new THREE.Group(); measurementSchematic.add(fan); fan.position.set(0, .2, 0);
  for (let i = 0; i < 5; i += 1) { const blade = box([.045, .32, .06], m.frame, fan, [0, 0, 0]); blade.rotation.z = i * Math.PI / 5; }
  const sensors = part("sensors", [.27, 2.83, 1.95], [.27, .18, .2]);
  cylinder(.055, .16, photoBlack, sensors, [0, 0, 0], 12);
  const humidity1 = part("humidity1", [-.11, 3.34, 1.95], [-.22, .5, 0]);
  box([.085, .15, .06], m.brass, humidity1, [0, 0, 0]);
  const humidity2 = part("humidity2", [2.15, 3.7, -1.3], [0, .35, 0]);
  box([.18, .32, .15], m.ivory, humidity2, [0, 0, 0]);
  const temperature = part("temperature", tankProbeLocations.temperature, [0, .38, 0]);
  temperature.name = "temperature-probe-in-nutrient-tank";
  cylinder(.06, .1, photoBlack, temperature, [0, 0, 0], 20);
  cylinder(.035, .80, photoSilver, temperature, [0, -.39, 0], 20).name = "immersed-tank-temperature-probe";
  const ecIn = part("ecIn", tankProbeLocations.ecIn, [0, .42, 0]);
  ecIn.name = "ec-in-probe-in-nutrient-tank";
  cylinder(.10, .1, photoBlack, ecIn, [0, 0, 0], 20);
  cylinder(.075, .63, m.ivory, ecIn, [0, -.30, 0], 20);
  cylinder(.045, .18, photoSilver, ecIn, [0, -.69, 0], 20).name = "immersed-tank-ec-electrode";
  // EC-OUT enters the vertical common fallpipe from the front, above the lamp.
  // Adapter geometry and immersion depth are explanatory, not measured hardware.
  const ecReturnSample = v(fallpipe.x, 1.99, fallpipe.z);
  const ecOut = part("ecOut", [fallpipe.x, ecReturnSample.y, fallpipe.z + .33], [.38, .22, .15]);
  ecOut.name = "ec-out-in-vertical-common-fallpipe-before-uvc";
  const frontProbeCylinder = (radius, length, mat, at, name) => {
    const object = cylinder(radius, length, mat, ecOut, at, 24); object.rotation.x = Math.PI / 2; if (name) object.name = name; return object;
  };
  frontProbeCylinder(.075, .32, m.ivory, [0, 0, -.08]);
  frontProbeCylinder(.035, .12, photoSilver, [0, 0, -.33], "return-ec-electrode");
  frontProbeCylinder(.10, .07, photoBlack, [0, 0, -.15]);
  const returnProbeAdapter = frontProbeCylinder(.11, .23, photoWhite, [0, 0, -.14], "schematic-return-probe-fitting");
  frontProbeCylinder(.05, .10, photoBlack, [0, 0, .13], "return-ec-cable-gland");
  const ecOutCableEndpoint = new THREE.Object3D(); ecOutCableEndpoint.position.set(0, 0, .18); ecOut.add(ecOutCableEndpoint);
  const leafPart = part("leaf", [1.5, 3.85, .5], [0, .3, 0]);
  box([.23, .07, .16], m.brass, leafPart, [0, 0, 0]); box([.23, .04, .16], m.dark, leafPart, [0, -.1, 0]);
  const controller = part("controller", [-4.6, 1.6, -2.9], [0, .85, 0]);
  const controllerBoard = createControllerBoardModel(THREE, { mesh, material });
  controller.add(controllerBoard.root);
  const controllerDisplayBase = controllerBoard.display.position.clone();
  const gateway = controllerBoard.gateway;
  const gatewayAnchor = new THREE.Object3D(); gatewayAnchor.position.set(0, .55, .12); gateway.add(gatewayAnchor);
  parts.set("gateway", { group: gateway, anchor: gatewayAnchor, base: gateway.getWorldPosition(v(0, 0, 0)), layer: "electronics" });
  root.updateMatrixWorld(true);
  const controllerPowerSupply = createControllerPowerSupply(THREE, {
    mesh, material,
    endpoint: controllerBoard.root.localToWorld(controllerBoard.portEndpoints.get("X2").clone()),
    terminals: controllerBoard.powerInputTerminals.map(point => controllerBoard.root.localToWorld(point.clone()))
  });
  root.add(controllerPowerSupply.root);
  const canopyBounds = new THREE.Box3();
  basilPlants.forEach(plant => canopyBounds.union(new THREE.Box3().setFromObject(plant.group)));
  // Fuller illustrative crowns within the retained presentation envelope. The
  // pots, roots, stems, petiole attachment points and neighbouring hardware do
  // not move. Keep this original envelope for Chaac/cable placement as well.
  // Actual blade silhouettes/angles are schematic, not measured plant growth.
  const foliageKeepouts = [
    new THREE.Box3(v(-.60, 2.85, 1.55), v(.60, 4.25, 2.75)), // Complete measurement insert.
    new THREE.Box3().setFromObject(humidity2).expandByScalar(.045),
    new THREE.Box3().setFromObject(leafPart).expandByScalar(.035),
    new THREE.Box3(v(2.04, 3.32, -1.43), v(2.36, 3.88, -1.17)), // HUMIDITY 2 lead.
    new THREE.Box3(v(1.38, 3.48, .38), v(1.92, 3.97, .62)), // Leaf-sensor lead.
    new THREE.Box3(v(1.86, 3.17, -.28), v(2.34, 3.55, .58))
  ];
  const foliageBox = new THREE.Box3();
  basilPlants.forEach(plant => plant.leafPairs.forEach((pair, pairIndex) => {
    pair.leaves.forEach((leaf, side) => {
      if (pairIndex === 5) return; // Retain the terminal crown/height exactly.
      const phase = plant.channelIndex * 1.13 + plant.plantIndex * .79 + pairIndex * .63 + side;
      const length = pairIndex < 5 ? .73 - pairIndex * .05 : (pairIndex % 2 ? .46 : .56);
      const pitch = (pairIndex < 5 ? [.34, .19, .025, -.16, -.34][pairIndex] : .18) + .085 * Math.sin(phase);
      const originalYaw = pair.azimuth + side * Math.PI;
      const roll = .13 * Math.sin(phase * 1.7), width = 1.20 + .12 * Math.cos(phase);
      const retainedRotation = leaf.rotation.clone(), retainedScale = leaf.scale.clone();
      let fitted = false;
      // Prefer full blades and natural angle variation. Only blades close to
      // sensors or the retained envelope shrink; all surfaces stay selectable.
      for (const turn of [0, .28, -.28, .55, -.55]) {
        leaf.rotation.set(pitch, originalYaw + .11 * Math.sin(phase) + turn, roll, "YXZ");
        for (let attempt = 0; attempt < 20; attempt += 1) {
          const size = length * Math.pow(.89, attempt);
          leaf.scale.set(size * width, size, size);
          leaf.updateWorldMatrix(true, false);
          foliageBox.setFromObject(leaf);
          if (canopyBounds.containsBox(foliageBox) && foliageKeepouts.every(box => !box.intersectsBox(foliageBox))) { fitted = true; break; }
        }
        if (fitted) break;
      }
      if (!fitted) { leaf.rotation.copy(retainedRotation); leaf.scale.copy(retainedScale); }
      leaf.userData.foliage = "illustrative-full-basil-crown-with-hardware-clearance";
      leaf.userData.foliageFitted = fitted;
    });
  }));
  const chaacExterior = createChaacCanopyModel(THREE, { mesh, material });
  const chaacLocalBounds = new THREE.Box3().setFromObject(chaacExterior.root);
  // Above the actual drawn crowns, over the right plant channel. The clearance
  // is a visual allowance, not a measured installation height or growing rule.
  const chaac = part("chaac", [channelCenters[2], canopyBounds.max.y + .45 - chaacLocalBounds.min.y, .3], [0, .45, 0]);
  chaac.name = "chaac-above-basil-canopy";
  chaac.add(chaacExterior.root);
  root.updateMatrixWorld(true);
  const chaacPowerStart = chaacExterior.powerConnector.endpoint.getWorldPosition(v(0, 0, 0));
  const chaacPowerDirection = chaacExterior.powerConnector.cableCurve.getTangent(1).transformDirection(chaacExterior.powerConnector.group.matrixWorld);
  const chaacPowerSupply = createChaacPowerSupply(THREE, { mesh, material, start: chaacPowerStart, exit: chaacPowerStart.clone().addScaledVector(chaacPowerDirection, .18), canopyBounds, floorCableY: floorOffset + .06 });
  root.add(chaacPowerSupply.root);
  const controllerStand = createControllerStandAssembly(THREE, { sceneRoot: root, controller, controllerPowerSupply, chaacPowerSupply });
  geometries.delete(controllerStand.replacedCableGeometry.previous);
  controllerStand.replacedCableGeometry.previous.dispose();
  geometries.add(controllerStand.replacedCableGeometry.next);
  ["controller", "gateway"].forEach(id => { const entry = parts.get(id); entry.base.copy(entry.group.getWorldPosition(v(0, 0, 0))); });
  const controllerPoint = point => controllerBoard.root.localToWorld(point.clone());
  const paths = { water: new THREE.Group(), airA: new THREE.Group(), airB: new THREE.Group(), data: new THREE.Group(), cables: new THREE.Group() };
  Object.values(paths).forEach(group => root.add(group));
  // Static sheathed electrical cables terminate at the local Aeroponic controller.
  // This depicts connectivity only: routing, colours, plugs and cable sizes are schematic.
  const cableJacket = material(0x242c28, { roughness: .9, metalness: 0, bumpMap: surfaceTextures.rubber, bumpScale: .0003 });
  const electricalCables = [];
  // Bounded rounded polylines do not overshoot the lowered floor between a
  // long vertical drop and a horizontal run. All bends stay in the waypoint
  // hull; this is schematic routing, not a physical cable bend-radius claim.
  const cablePath = points => {
    const vertices = points.map(point => point.isVector3 ? point.clone() : v(...point));
    const curve = new THREE.CurvePath(); let previous = vertices[0];
    for (let index = 1; index < vertices.length - 1; index += 1) {
      const corner = vertices[index], next = vertices[index + 1];
      const trim = Math.min(.08, corner.distanceTo(vertices[index - 1]) / 4, corner.distanceTo(next) / 4);
      const before = corner.clone().lerp(vertices[index - 1], trim / corner.distanceTo(vertices[index - 1]));
      const after = corner.clone().lerp(next, trim / corner.distanceTo(next));
      curve.add(new THREE.LineCurve3(previous.clone(), before));
      curve.add(new THREE.QuadraticBezierCurve3(before, corner, after)); previous = after;
    }
    curve.add(new THREE.LineCurve3(previous.clone(), vertices[vertices.length - 1])); return curve;
  };
  const addCable = (sourceIds, type, points, targetRef) => {
    points = points.map(([x, y, z]) => [x, y === .18 ? y + floorOffset : y, z]);
    const group = new THREE.Group(); group.name = `schematic-${type}-cable-${sourceIds[0]}`;
    group.userData = { cableType: type, sourceIds, targetId: "controller", targetRef, controlDomain: "aeroponic-stm32", schematic: true };
    paths.cables.add(group);
    const localEndpoint = controllerBoard.portEndpoints.get(targetRef);
    const endpoint = controllerPoint(localEndpoint);
    const { x, z } = endpoint;
    const upperIndex = ["J17", "J7", "J8"].indexOf(targetRef);
    const routingX = 1.14 + Math.max(0, upperIndex) * .08;
    const localFloorY = controllerBoard.root.worldToLocal(v(x, .2 + floorOffset, z)).y;
    const { x: lx, y: ly, z: lz } = localEndpoint;
    const approachLocal = upperIndex >= 0
      ? [[routingX, localFloorY, lz + .12], [routingX, ly + .35, lz + .12], [lx, ly + .35, lz + .12], [lx, ly + .18, lz]]
      : [[lx, localFloorY, lz], [lx, ly - .18, lz]];
    const approach = approachLocal.map(point => controllerPoint(v(...point)).toArray());
    const radius = type === "power" ? .027 : .012;
    const curve = cablePath([...points, ...approach, endpoint.toArray()]);
    mesh(new THREE.TubeGeometry(curve, (points.length + approach.length) * 18, radius, 7, false), cableJacket, group);
    const cableMesh = group.children[0];
    // Continue the jacket into the socket mouth; the external routing endpoint
    // is not a floating electrical connection. No pin-level wiring is implied.
    if (targetRef !== "X1") tube([endpoint.toArray(), controllerPoint(localEndpoint.clone().add(v(0, upperIndex >= 0 ? -.06 : .06, 0))).toArray()], .012, cableJacket, group);
    const motorLeads = targetRef === "X1" ? controllerBoard.ports.get("X1").children.map(pad => {
      const end = pad.getWorldPosition(v(0, 0, 0));
      const localEnd = controllerBoard.root.worldToLocal(end.clone());
      const lead = tube([endpoint.toArray(), controllerPoint(localEnd.add(v(0, -.04, .04))).toArray(), end.toArray()], .008, cableJacket, group);
      return { terminal: pad.userData.motorTerminal, curve: lead, end };
    }) : [];
    const cable = { group, sourceIds, targetId: "controller", targetRef, type, curve, motorLeads, start: v(...points[0]), end: endpoint,
      setSource(anchor, exit) {
        if (this.start.distanceTo(anchor) < 1e-10 && this.exit?.distanceTo(exit) < 1e-10) return;
        this.start.copy(anchor); this.exit = exit.clone();
        const guided = new THREE.CurvePath();
        guided.add(new THREE.LineCurve3(anchor.clone(), exit.clone()));
        guided.add(cablePath([exit.clone(), ...points.slice(2), ...approach, endpoint.clone()]));
        this.curve = guided;
        geometries.delete(cableMesh.geometry); cableMesh.geometry.dispose();
        cableMesh.geometry = new THREE.TubeGeometry(guided, (points.length + approach.length) * 12, radius, 7, false); geometries.add(cableMesh.geometry);
      }
    };
    electricalCables.push(cable); return cable;
  };
  const pumpPowerStart = pumpPowerEndpoint.getWorldPosition(v(0, 0, 0)), pumpPowerExit = pumpPowerStart.clone().add(v(-.18, 0, 0));
  const pumpPowerCable = addCable(["pump"], "power", [pumpPowerStart.toArray(), pumpPowerExit.toArray(), [-4.95, .18, pumpPowerStart.z], [-4.95, .18, -2.5]], "X1");
  pumpPowerCable.setSource(pumpPowerStart, pumpPowerExit);
  const humidityCable = addCable(["humidity1", "sensors"], "sensor", [[-.037638, 3.72, 2.09621625], [-.037638, 4.02, 2.09621625], [-.6, 4.02, 2.8], [-2.25, 3.3, 2.8], [-2.65, 1.3, 2.8], [-3.1, .18, 2.8], [-3.1, .18, -2.5]], "J2");
  const bldcCable = addCable(["air"], "measurement-bldc-custom", [[-.24, 3.49425, 1.65], [-.4, 3.49425, 1.45], [-.7, 3.7, 1.1], [-.8, 3.7, 2.8], [-2.25, 3.25, 2.8], [-2.7, 3.0, 2.8], [-2.7, .18, 2.8], [-3.0, .18, -2.5]], "J6");
  addCable(["humidity2"], "sensor", [[2.15, 3.7, -1.3], [2.3, 3.4, -1.3], [2.3, .18, -2.6], [-3.1, .18, -2.6]], "J16");
  addCable(["temperature"], "sensor", [[-1.35, tankLidHeight + .09, 4.25], [-1.35, tankLidHeight + .2, 4.25], [-2.65, tankLidHeight + .2, 4.25], [-3.1, .18, 4.25], [-3.1, .18, -2.5]], "J4");
  addCable(["ecIn"], "sensor", [[-.75, tankLidHeight + .09, 4.25], [-.75, tankLidHeight + .24, 4.25], [-2.7, tankLidHeight + .24, 4.25], [-3.2, .18, 4.25], [-3.2, .18, -2.5]], "J7");
  addCable(["ecOut"], "sensor", [ecOutCableEndpoint.getWorldPosition(v(0, 0, 0)).toArray(), [0, 1.99, 3.87], [-2.75, 2.0, 3.95], [-3.3, .18, 3.95], [-3.3, .18, -2.5]], "J8");
  addCable(["leaf"], "sensor", [[1.5, 3.85, .5], [1.8, 3.6, .5], [2.25, 3.2, -.2], [2.4, .18, -2.6], [-3.2, .18, -2.6]], "J17");
  const curves = {
    suction: tube([tankIntake.toArray(), tankOutlet.toArray(), [tankOutlet.x, bottomPipeY, tankOutlet.z], [-2.75, bottomPipeY, tankOutlet.z], [-2.75, microfilterPorts.inlet.y, tankOutlet.z], filterInletLead.toArray(), microfilterPorts.inlet.toArray()], .035, photoBlack, paths.water, true),
    suctionAfterFilter: tube([microfilterPorts.outlet.toArray(), filterOutletLead.toArray(), [-4.25, microfilterPorts.outlet.y, 4.6], [-4.25, .45 + floorOffset, 3.65], [pumpInlet.x, pumpInlet.y, pumpInlet.z + .35], pumpInlet.toArray()], .028, photoBlack, paths.water),
    water: tube([pumpOutlet.toArray(), pumpOutlet.clone().add(v(.18, 0, 0)).toArray(), [-2.6, pumpOutlet.y, 2.42], [-2.4, pumpOutlet.y, 1.95], [-2.35, 1.70, 1.95], [-.5, 1.70, 1.95], [-.5, 1.85, 1.95], [-.5, 1.85, 2.82]], .028, photoBlack, paths.water, true),
    returnToUvc: tube([[fallpipe.x, 2.06, fallpipe.z], ecReturnSample.toArray(), uvcPorts.inlet.toArray()], fallpipe.radius, photoWhite, paths.water, true),
    returnFromUvc: tube([uvcPorts.outlet.toArray(), [fallpipe.x, 1.02, fallpipe.z]], fallpipe.radius, photoWhite, paths.water),
    airA: tube([[.65, 3.61, 1.95], [.21, 3.61, 1.95], [.2, 3.32, 1.95], [-.2, 3.32, 1.95], [-.21, 3.61, 1.95], [-.65, 3.61, 1.95]], .022, m.airA, paths.airA),
    airB: tube([[-.28, 2.7, 1.95], [-.21, 2.96, 1.95], [-.15, 3.23, 1.95], [.15, 3.23, 1.95], [.21, 2.96, 1.95], [.28, 2.7, 1.95]], .022, m.airB, paths.airB),
    data: tube([controllerBoard.gatewayInterfaceEndpoints.get("JP9"), controllerBoard.gatewayInterfaceEndpoints.get("JP10")].map(point => controllerPoint(point).toArray()), .008, m.data, paths.data)
  };
  const returnJunction = v(fallpipe.x, 2.06, fallpipe.z);
  const returnCommonPipe = paths.water.children.find(object => object.geometry?.parameters?.path === curves.returnToUvc);
  const returnBranches = channelCenters.map((x, index) => {
    const start = v(x, 2.13, 2.77);
    const curve = tube([start.toArray(), [x, 2.09, 2.93], [x, returnJunction.y, returnJunction.z], returnJunction.toArray()].filter((point, i, all) => !i || point.some((value, axis) => value !== all[i - 1][axis])), .10, photoWhite, paths.water, true);
    curves[`returnBranch${index + 1}`] = curve;
    return { from: `chamber-${index + 1}`, to: "return-junction", start, end: returnJunction.clone(), curve };
  });
  const hydraulicTopology = {
    microfilterPorts, tankIntake, tankOutlet, uvcPorts, returnJunction, returnDrive: "gravity", uvcPipe, uvcLamp,
    ecReturnSample, ecOutCableEndpoint, tankProbeLocations, tankLidHeight, tankWater, tankCover, returnCommonPipe,
    tankFloor, floorHeight, floorThickness, suctionFloorEntry, tankCapacity, tankLowFloorY,
    edges: [
      { from: "reservoir", to: "microfilter", kind: "suction", curve: curves.suction },
      { from: "microfilter", to: "pump", kind: "suction", curve: curves.suctionAfterFilter },
      ...returnBranches.map(edge => ({ ...edge, kind: "return" })),
      { from: "return-junction", to: "uvc", kind: "return", curve: curves.returnToUvc },
      { from: "uvc", to: "reservoir", kind: "return", curve: curves.returnFromUvc }
    ]
  };
  const pressureRing = { nodes: ringNodes, edges: ringEdges, tee, group: nozzle, feed: { from: "pump-outlet", to: tee.id, toPort: "feed", curve: curves.water, start: pumpOutlet.clone(), end: tee.ports.feed.clone().add(nozzle.position) } };
  const tokens = [];
  Object.entries(curves).forEach(([key, curve]) => {
    const kind = key.startsWith("return") ? "return" : key.startsWith("suction") ? "suction" : key;
    const count = kind === "data" ? 1 : 7;
    for (let i = 0; i < count; i += 1) { const token = mesh(new THREE.SphereGeometry(kind === "data" ? .009 : .045, 7, 5), m.token, paths[["return", "suction"].includes(kind) ? "water" : kind]); tokens.push({ object: token, curve, kind, offset: i / count }); }
  });
  ringEdges.forEach(({ curve }) => {
    for (let i = 0; i < 3; i += 1) { const object = mesh(new THREE.SphereGeometry(.04, 7, 5), m.token, nozzle); tokens.push({ object, curve, kind: "water", offset: i / 3 }); }
  });
  const selection = new THREE.BoxHelper(rootsPart, 0xe1ce8e); root.add(selection); materials.add(selection.material); geometries.add(selection.geometry);
  selection.visible = false;
  let exploded = 0, measurementAssembly = null, measurementConnections = null;
  let lastView = {};
  root.updateMatrixWorld(true);
  photoChannels.forEach(channel => {
    channel.cameraCutaway = false;
    channel.cameraBounds = new THREE.Box3().setFromObject(channel.group);
  });
  const applyChamberCutaway = () => {
    const manual = lastView.cutaway === true;
    photoChannels.forEach(channel => { channel.lid.visible = true; channel.rightWall.visible = true; channel.viewWindow.material = manual || channel.cameraCutaway ? cutWindowMaterial : photoWhite; });
    const anyCutaway = manual || !chambersPart.visible || photoChannels.some(channel => channel.cameraCutaway);
    roots.visible = anyCutaway; nozzleInternals.visible = anyCutaway;
  };
  return {
    root, parts, shell, roof, glazing, paths, tokens, mist, fan, selection, base, explodedPieces,
    photoChannels, netPotAssemblies, rootSystems, basilPlants, supportFrame, pressureRing, hydraulicTopology, waterMaintenance, prefilterExterior, pumpExterior, pumpNameplate, pumpPorts, pumpPowerEndpoint, floorLevel, floorOffset, tankDepthExtension, controllerBoard, controllerDisplayBase, nozzleInternals, nozzleAssemblies, electricalCables,
    measurementMount, measurementSchematic, chaacExterior, chaacPowerSupply, controllerPowerSupply, controllerStand,
    // Only the chamber windows respond to proximity. The roof, UVC housing,
    // other systems and CAD explosion are deliberately unaffected.
    updateCameraCutaway(cameraPosition) {
      const valid = cameraPosition && [cameraPosition.x, cameraPosition.y, cameraPosition.z].every(Number.isFinite);
      photoChannels.forEach(channel => {
        channel.cameraCutaway = chambersPart.visible && nextCameraCutaway(valid ? channel.cameraBounds.distanceToPoint(cameraPosition) : Infinity, channel.cameraCutaway);
      });
      applyChamberCutaway();
      return lastView.cutaway !== true && photoChannels.some(channel => channel.cameraCutaway);
    },
    // The assembly owns its geometry/material cleanup and its reversible, documented
    // component offsets. It is placed in the user-confirmed net-pot location, not on a new stand.
    setMeasurementAssembly(assembly) {
      if (measurementAssembly) { measurementMount.remove(measurementAssembly.root); measurementAssembly.dispose(); }
      measurementAssembly = assembly || null;
      measurementSchematic.visible = !measurementAssembly;
      measurementConnections = null;
      if (measurementAssembly) {
        measurementMount.add(measurementAssembly.root);
        measurementConnections = createMeasurementConnections(THREE, measurementAssembly, { mesh, material });
      }
      this.setView(lastView);
    },
    get measurementConnections() { return measurementConnections; },
    setView({ roofVisible = true, cutaway = false, layers = {}, selected, explode = false, measurementExploded = false, selectedNozzle = "", selectedPort = "" }) {
      lastView = { roofVisible, cutaway, layers, selected, explode, measurementExploded, selectedNozzle, selectedPort };
      const assemblyExploded = measurementExploded === true && measurementPartIds.includes(selected);
      const systemExploded = explode && !measurementPartIds.includes(selected);
      roof.visible = roofVisible; glazing.visible = !cutaway;
      [shell, glazing].forEach(group => group.children.forEach(object => {
        if (object.userData.openHouseHidden) object.visible = roofVisible;
      }));
      parts.forEach(({ group, layer }) => { group.visible = layers[layer] !== false; });
      paths.water.visible = layers.hydraulics !== false;
      // The original assembly replaces all schematic air-path geometry. These
      // coloured teaching hoses are not real CAD tubes and must not cross it.
      paths.airA.visible = paths.airB.visible = !measurementAssembly && layers.climate !== false;
      paths.data.visible = layers.electronics !== false;
      paths.cables.visible = layers.electronics !== false;
      chaacPowerSupply.root.visible = layers.electronics !== false;
      chaacPowerSupply.cable.visible = layers.electronics !== false;
      controllerPowerSupply.root.visible = layers.electronics !== false;
      electricalCables.forEach(cable => { cable.group.visible = parts.get(cable.sourceIds[0]).group.visible; });
      applyChamberCutaway();
      const uvcCutaway = cutaway && selected === "uvc";
      uvcPipe.material = uvcCutaway ? cutWindowMaterial : photoWhite; uvcLamp.visible = uvcCutaway;
      const tankCutaway = cutaway && ["temperature", "ecIn", "reservoir", "microfilter", "refill", "drain"].includes(selected);
      tankSectionSurfaces.forEach(surface => { surface.material = tankCutaway ? cutWindowMaterial : photoBlack; });
      tankWater.material = tankCutaway ? cutWindowMaterial : tankWaterMaterial;
      returnProbeAdapter.material = cutaway && selected === "ecOut" ? cutWindowMaterial : photoWhite;
      returnCommonPipe.material = cutaway && selected === "ecOut" ? cutWindowMaterial : photoWhite;
      exploded = systemExploded ? 1 : 0;
      explodedPieces.forEach(({ object, base: initial, offset }) => { const owner = findScenePart(object); object.position.copy(initial).addScaledVector(offset, owner === "air" ? (assemblyExploded ? 1 : 0) : owner === selected ? exploded : 0); });
      plants.position.y = 0; roots.position.y = 0; nozzle.position.y = 0;
      const controllerExploded = systemExploded && selected === "controller";
      controllerBoard.display.position.copy(controllerDisplayBase);
      if (controllerExploded) controllerBoard.display.position.z -= .65;
      chamberShell.position.y = .16 + (assemblyExploded ? .5 : 0);
      if (measurementAssembly) measurementAssembly.setExploded(assemblyExploded);
      // Existing reference shaft is already inserted in the CAD sensor channel.
      // Hide the generic fallback blocks, not the logical selectable sensor roles.
      [humidity1, sensors].forEach(group => group.children.forEach(object => { if (object.isMesh) object.visible = !measurementAssembly; }));
      if (measurementConnections) {
        root.updateMatrixWorld(true);
        const usb = measurementConnections.usbAnchor.getWorldPosition(v(0, 0, 0));
        const direction = v(-.6084147482, 0, -.7936192376);
        bldcCable.setSource(usb, usb.clone().addScaledVector(direction, .16));
        const sensor = measurementConnections.sensorAnchor.getWorldPosition(v(0, 0, 0));
        const exit = measurementConnections.sensorExit.getWorldPosition(v(0, 0, 0)).add(v(0, .1, 0));
        humidityCable.setSource(sensor, exit);
      }
      parts.forEach(({ group }) => group.traverse(object => {
        if (object.isMesh && object.material.emissive) { object.userData.selected = false; }
      }));
      const target = parts.get(selected);
      selection.visible = Boolean(target && target.group.visible);
      const nozzlePart = selected === "nozzle" && nozzleAssemblies.find(item => item.id === selectedNozzle);
      const portPart = selected === "controller" && controllerBoard.ports.get(selectedPort);
      if (target) selection.setFromObject(nozzlePart ? nozzlePart.sprayHead : portPart || target.group);
      root.updateMatrixWorld(true);
    },
    animate(time, playing, selected, visual = null) {
      const pumpActive = !visual || visual.pumpActive === true;
      const airPath = visual ? visual.airPath : selected === "air" ? "reference" : selected === "sensors" ? "root" : null;
      mist.visible = playing && pumpActive && nozzleInternals.visible;
      // Only travel down within the owning chamber; never rotate the complete mist
      // field around the greenhouse origin or imply measured droplet dimensions.
      if (mist.visible) {
        for (let i = 0; i < 216; i += 1) mistCoordinates[i * 3 + 1] = 2.25 + (1 - ((time * .3 + ((i * 11) % 31) / 31) % 1)) * .38;
        mistGeometry.attributes.position.needsUpdate = true;
      }
      tokens.forEach(({ object, curve, kind, offset }) => {
        const isAir = kind === "airA" || kind === "airB";
        const active = kind === "airA" ? airPath === "reference" : kind === "airB" ? airPath === "root" : true;
        const enabled = ["water", "return", "suction"].includes(kind) ? pumpActive : kind === "data" ? !(visual && visual.connectionLost) : true;
        object.visible = playing && enabled && (!isAir || (active && !measurementAssembly));
        if (object.visible) object.position.copy(curve.getPointAt((time * .1 + offset) % 1));
      });
      if (playing && airPath) fan.rotation.z = time * 3;
    },
    dispose() { if (modelDisposed) return; modelDisposed = true; if (measurementAssembly) measurementAssembly.dispose(); pumpNameplate.dispose(); controllerBoard.displaySurface.dispose(); geometries.forEach(geometry => geometry.dispose()); materials.forEach(mat => mat.dispose()); surfaceTextures.dispose(); root.clear(); }
  };
}

export function findScenePart(object) {
  let current = object;
  while (current) { if (current.userData && current.userData.partId) return current.userData.partId; current = current.parent; }
  return null;
}

export default function GreenhouseScene3D({ selected = "reservoir", onSelect, showPartPicker = true, playing = false, reducedMotion = false, fallback = null, visual = null, afterModel = null, displayFrame = null, displayStatus = "closed", onDisplayStart, onDisplayClose, onDisplayPointer }) {
  const [quality, setQuality] = useState("high");
  const host = useRef(null), stage = useRef(null), engine = useRef(null), markers = useRef({}), latest = useRef({ selected, onSelect, playing, reducedMotion, visual });
  latest.current = { selected, onSelect, playing, reducedMotion, visual, displayFrame, displayStatus, onDisplayPointer, quality };
  const [available, setAvailable] = useState(null);
  const [screenshotStatus, setScreenshotStatus] = useState("pending");
  const [roofVisible, setRoofVisible] = useState(false), [cutaway, setCutaway] = useState(false), [explode, setExplode] = useState(false);
  const [measurementExploded, setMeasurementExploded] = useState(false);
  const [automaticCutaway, setAutomaticCutaway] = useState(false);
  const [labels, setLabels] = useState(false), [mode, setMode] = useState("orbit");
  const [layers, setLayers] = useState({ hydraulics: true, climate: true, electronics: true, plants: true });
  const [selectionName, setSelectionName] = useState("");
  const [selectedNozzle, setSelectedNozzle] = useState(""), [hoveredNozzle, setHoveredNozzle] = useState("");
  const [selectedPort, setSelectedPort] = useState(""), [hoveredPort, setHoveredPort] = useState("");
  const [controllerSide, setControllerSide] = useState("ports");
  const [cadStatus, setCadStatus] = useState("pending");
  const previousSelection = useRef(selected);
  const previousExplosion = useRef({ system: false, measurement: false });
  const measurementPreviousView = useRef(null), requestedMeasurementOpen = useRef(false);
  const requestedNozzleInspection = useRef(false);

  useEffect(() => {
    if (!host.current || typeof window.WebGL2RenderingContext === "undefined") { setAvailable(false); return undefined; }
    const container = host.current;
    let disposed = false;
    let cleanup = null;
    const assetRequest = new AbortController();
    const release = () => { const action = cleanup; cleanup = null; if (action) action(); };
    const initialize = async () => {
    const [THREE, cursorZoom] = await Promise.all([import("three"), import("./CursorOrbitZoom")]);
    if (disposed) return;
    const v = (x, y, z) => new THREE.Vector3(x, y, z);
    let renderer;
    try { renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: "high-performance" }); }
    catch (_) { setAvailable(false); return undefined; }
    renderer.outputColorSpace = THREE.SRGBColorSpace; renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = .98;
    renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.domElement.tabIndex = 0;
    renderer.domElement.setAttribute("role", "img");
    renderer.domElement.setAttribute("aria-label", "Dreidimensionales FEEDER-Gewächshaus. Ziehen zum Drehen, Mausrad zum Detail unter dem Zeiger zoomen, Bauteil anklicken. Innenansicht: W A S D zum Gehen.");
    container.appendChild(renderer.domElement);
    cleanup = () => { renderer.dispose(); renderer.forceContextLoss(); if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement); };
    const scene = new THREE.Scene(), backdrop = createGreenhouseBackdropTexture(THREE);
    scene.background = backdrop; scene.backgroundIntensity = .85; scene.backgroundBlurriness = .05;
    scene.fog = new THREE.Fog(0x86967c, 38, 76);
    const releaseBackdropRenderer = cleanup;
    cleanup = () => { scene.background = null; backdrop.dispose(); releaseBackdropRenderer(); };
    const camera = new THREE.PerspectiveCamera(43, 1, .05, 90);
    const { hemisphere, sun, fill } = createGreenhouseLighting(THREE);
    scene.add(hemisphere, sun, sun.target, fill, fill.target);
    const environment = createGreenhouseEnvironment(THREE, renderer);
    scene.environment = environment.texture; scene.environmentIntensity = .45;
    const releaseEnvironmentRenderer = cleanup;
    cleanup = () => { scene.environment = null; environment.dispose(); sun.shadow.dispose(); releaseEnvironmentRenderer(); };
    const model = createGreenhouseModel(THREE); scene.add(model.root);
    const releaseRenderer = cleanup;
    cleanup = () => { model.dispose(); releaseRenderer(); };
    const raycaster = new THREE.Raycaster(), pointer = new THREE.Vector2(), projected = new THREE.Vector3();
    const orbit = { target: v(...greenhousePresentation.target), radius: greenhousePresentation.radius, theta: greenhousePresentation.theta, phi: greenhousePresentation.phi, nozzleDetail: false };
    const walk = { position: v(0, 1.8, 5.4), yaw: Math.PI, pitch: 0 };
    let activeMode = "orbit", frame = 0, last = 0, dirty = true, pointerStart = null, pinchDistance = null, cameraCutawayActive = false;
    const pointers = new Map();
    let displayGesture = null;
    const releaseDisplayCapture = id => {
      if (renderer.domElement.hasPointerCapture?.(id)) renderer.domElement.releasePointerCapture(id);
    };
    const emitDisplayPointer = (callback, value) => { try { return callback(value) !== false; } catch (_) { return false; } };
    const cancelDisplayGesture = () => {
      const gesture = displayGesture; displayGesture = null;
      if (!gesture) return;
      emitDisplayPointer(gesture.callback, { kind: "cancel" }); releaseDisplayCapture(gesture.id);
    };
    const updateCameraPose = () => {
      const fieldOfView = orbit.nozzleDetail ? 60 : 43;
      if (camera.fov !== fieldOfView) { camera.fov = fieldOfView; camera.updateProjectionMatrix(); }
      if (activeMode === "orbit") camera.position.copy(orbit.target).add(v(Math.sin(orbit.phi) * Math.sin(orbit.theta), Math.cos(orbit.phi), Math.sin(orbit.phi) * Math.cos(orbit.theta)).multiplyScalar(orbit.radius * (orbit.nozzleDetail ? 1 : Math.max(1, .95 / camera.aspect))));
      else camera.position.copy(walk.position);
      if (activeMode === "orbit") camera.lookAt(orbit.target);
      else camera.lookAt(walk.position.clone().add(v(Math.sin(walk.yaw) * Math.cos(walk.pitch), Math.sin(walk.pitch), Math.cos(walk.yaw) * Math.cos(walk.pitch))));
      camera.updateMatrixWorld(true);
    };
    const render = time => {
      if (disposed) return;
      frame = 0;
      const motion = latest.current.playing && !latest.current.reducedMotion;
      updateCameraPose();
      const cameraCutaway = model.updateCameraCutaway(camera.position);
      if (cameraCutaway !== cameraCutawayActive) { cameraCutawayActive = cameraCutaway; setAutomaticCutaway(cameraCutaway); }
      model.animate((time || 0) / 1000, motion, latest.current.selected, latest.current.visual);
      scene.updateMatrixWorld(true); camera.updateMatrixWorld(true);
      const rect = container.getBoundingClientRect();
      model.parts.forEach(({ anchor, group }, id) => {
        const label = markers.current[id]; if (!label) return;
        anchor.getWorldPosition(projected); projected.project(camera);
        label.hidden = !group.visible || projected.z < -1 || projected.z > 1 || Math.abs(projected.x) > .98 || Math.abs(projected.y) > .97;
        if (!label.hidden) { label.style.left = `${(projected.x * .5 + .5) * rect.width}px`; label.style.top = `${(-projected.y * .5 + .5) * rect.height}px`; }
      });
      renderer.render(scene, camera); dirty = false; last = time;
      if (motion && !document.hidden) frame = window.requestAnimationFrame(render);
    };
    const requestRender = () => { dirty = true; if (!frame && !disposed) frame = window.requestAnimationFrame(render); };
    const resize = () => {
      const width = container.clientWidth || 640, height = container.clientHeight || 540;
      applySceneRenderQuality(renderer, sun, { width, height, devicePixelRatio: window.devicePixelRatio, quality: latest.current.quality });
      camera.aspect = width / height; camera.updateProjectionMatrix(); requestRender();
    };
    const focusObject = (object, minimum = 2.2) => {
      cancelDisplayGesture();
      const bounds = new THREE.Box3().setFromObject(object), size = bounds.getSize(v(0, 0, 0));
      bounds.getCenter(orbit.target); orbit.radius = clamp(Math.max(size.x, size.y, size.z) * 2.8, minimum, 14); orbit.phi = 1.05; orbit.nozzleDetail = false;
      activeMode = "orbit"; setMode("orbit"); requestRender();
    };
    const focusController = (side = "ports", ref = "") => {
      const object = ref ? model.controllerBoard.ports.get(ref) : side === "display" ? model.controllerBoard.display : model.parts.get("controller").group;
      if (!object) return;
      focusObject(object, ref ? .85 : 2.8);
      if (side === "display") orbit.radius = 2.5;
      orbit.theta = controllerFocusTheta(THREE, model.controllerBoard.root, side);
      orbit.phi = side === "display" ? 1.48 : ref ? (["J2", "J16", "J4", "J6"].includes(ref) ? 2.05 : 1.05) : 1.72;
      if (ref) model.selection.setFromObject(object);
      setControllerSide(side); requestRender();
    };
    const focus = id => {
      const entry = model.parts.get(id);
      if (id === "controller") focusController();
      else if (entry) {
        focusObject(entry.group);
        if (id === "chaac") { orbit.theta = -1.15; orbit.phi = 1.05; orbit.radius *= .8; requestRender(); }
        if (["temperature", "ecIn"].includes(id)) { orbit.theta = 0; orbit.phi = 1.5; requestRender(); }
        if (["osmosis", "refill", "drain"].includes(id)) { orbit.theta = .35; orbit.phi = 1.2; requestRender(); }
        if (["uvc", "ecOut"].includes(id)) {
          // Never inherit the controller's rear-facing display angle. Frame the
          // shared vertical section from its clear front, above the tank lid.
          orbit.target.copy(model.hydraulicTopology.returnJunction).lerp(model.hydraulicTopology.uvcPorts.outlet, .5);
          orbit.theta = 0; orbit.phi = 1.5; orbit.radius = 2.5; requestRender();
        }
      }
    };
    const focusNozzle = id => {
      const entry = model.nozzleAssemblies.find(item => item.id === id); if (!entry) return;
      entry.sprayHead.getWorldPosition(orbit.target); orbit.target.y = 2.65;
      // Inspect through that chamber's side window from the gap between channels,
      // rather than putting the camera inside the neighbouring opaque chamber.
      orbit.radius = 1.25; orbit.theta = -Math.PI / 2; orbit.phi = 1.53; orbit.nozzleDetail = true;
      activeMode = "orbit"; setMode("orbit"); model.selection.setFromObject(entry.sprayHead); requestRender();
    };
    const reset = () => { cancelDisplayGesture(); activeMode = "orbit"; setMode("orbit"); orbit.target.set(...greenhousePresentation.target); orbit.radius = greenhousePresentation.radius; orbit.theta = greenhousePresentation.theta; orbit.phi = greenhousePresentation.phi; orbit.nozzleDetail = false; setHoveredNozzle(""); setSelectedNozzle(""); setHoveredPort(""); setSelectedPort(""); requestRender(); };
    const move = (forward, side = 0) => {
      walk.position.x = clamp(walk.position.x + Math.sin(walk.yaw) * forward + Math.cos(walk.yaw) * side, -5.15, 5.15);
      walk.position.z = clamp(walk.position.z + Math.cos(walk.yaw) * forward - Math.sin(walk.yaw) * side, -5.6, 5.7);
      requestRender();
    };
    const displayPoint = event => {
      const rect = renderer.domElement.getBoundingClientRect();
      if (!(rect.width > 0 && rect.height > 0) || !Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) return null;
      updateCameraPose(); model.root.updateMatrixWorld(true);
      pointer.set((event.clientX - rect.left) / rect.width * 2 - 1, -(event.clientY - rect.top) / rect.height * 2 + 1);
      raycaster.setFromCamera(pointer, camera);
      const hit = findVisibleDisplayIntersection(raycaster.intersectObject(model.root, true), model.controllerBoard.displaySurface.surface);
      return hit ? displayUvToPixel(hit.uv) : null;
    };
    const pointerDown = event => {
      if (displayGesture) { if (displayGesture.id !== event.pointerId) cancelDisplayGesture(); return; }
      if (event.button !== undefined && event.button !== 0) return;
      setHoveredNozzle(""); setHoveredPort("");
      if (!pointers.size && latest.current.displayStatus === "ready" && model.controllerBoard.displaySurface.ready && typeof latest.current.onDisplayPointer === "function") {
        const point = displayPoint(event);
        if (point) {
          event.preventDefault(); pointerStart = null; pinchDistance = null;
          renderer.domElement.focus({ preventScroll: true }); renderer.domElement.setPointerCapture(event.pointerId);
          const callback = latest.current.onDisplayPointer;
          displayGesture = { id: event.pointerId, callback };
          if (!emitDisplayPointer(callback, { kind: "down", ...point })) cancelDisplayGesture();
          return;
        }
      }
      renderer.domElement.focus({ preventScroll: true }); renderer.domElement.setPointerCapture(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      pointerStart = { x: event.clientX, y: event.clientY, moved: false };
      if (pointers.size === 2) { const points = [...pointers.values()]; pinchDistance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y); }
    };
    const pointerMove = event => {
      if (displayGesture) {
        if (displayGesture.id !== event.pointerId) return;
        event.preventDefault();
        const point = displayPoint(event);
        if (!point || !emitDisplayPointer(displayGesture.callback, { kind: "move", ...point })) cancelDisplayGesture();
        return;
      }
      const previous = pointers.get(event.pointerId);
      if (!previous) {
        const rect = renderer.domElement.getBoundingClientRect(); pointer.set((event.clientX - rect.left) / rect.width * 2 - 1, -(event.clientY - rect.top) / rect.height * 2 + 1);
        raycaster.setFromCamera(pointer, camera);
        const intersections = raycaster.intersectObject(model.root, true);
        const hit = findVisibleNozzleIntersection(intersections);
        setHoveredNozzle(hit ? hit.object.userData.nozzleId : "");
        setHoveredPort(latest.current.selected === "controller" ? findVisibleControllerPort(intersections) : ""); return;
      }
      setHoveredNozzle(""); setHoveredPort("");
      const dx = event.clientX - previous.x, dy = event.clientY - previous.y;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointerStart && Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y) > 5) pointerStart.moved = true;
      if (pointers.size === 2) {
        const points = [...pointers.values()], distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        if (pinchDistance) orbit.radius = sceneZoomRadius(orbit.radius, pinchDistance / Math.max(20, distance), orbit.nozzleDetail);
        pinchDistance = distance;
      } else if (activeMode === "orbit") { orbit.theta -= dx * .006; orbit.phi = sceneOrbitPhi(orbit.phi, dy * .005, latest.current.selected); }
      else { walk.yaw -= dx * .005; walk.pitch = clamp(walk.pitch - dy * .005, -.95, .95); }
      requestRender();
    };
    const pointerUp = event => {
      if (displayGesture) {
        if (displayGesture.id !== event.pointerId) return;
        event.preventDefault();
        const point = event.type === "pointerup" ? displayPoint(event) : null;
        if (!point) cancelDisplayGesture();
        else {
          const gesture = displayGesture; displayGesture = null;
          if (!emitDisplayPointer(gesture.callback, { kind: "up", ...point })) emitDisplayPointer(gesture.callback, { kind: "cancel" });
          releaseDisplayCapture(gesture.id);
        }
        return;
      }
      const click = pointerStart && !pointerStart.moved && pointers.size === 1 && event.type !== "pointercancel";
      pointers.delete(event.pointerId); pinchDistance = null;
      if (renderer.domElement.hasPointerCapture(event.pointerId)) renderer.domElement.releasePointerCapture(event.pointerId);
      if (click) {
        const rect = renderer.domElement.getBoundingClientRect(); pointer.set((event.clientX - rect.left) / rect.width * 2 - 1, -(event.clientY - rect.top) / rect.height * 2 + 1);
        updateCameraPose(); model.root.updateMatrixWorld(true);
        raycaster.setFromCamera(pointer, camera);
        const intersections = raycaster.intersectObject(model.root, true);
        const measurementHit = findVisibleMeasurementIntersection(intersections);
        if (measurementHit) {
          setSelectedNozzle(""); setHoveredNozzle(""); setSelectedPort(""); setHoveredPort("");
          setSelectionName("Messeinrichtung");
          if (latest.current.onSelect) latest.current.onSelect("air");
          focus("air"); pointerStart = null; return;
        }
        const portRef = findVisibleControllerPort(intersections);
        if (portRef) {
          setSelectedPort(portRef); setHoveredPort(""); setSelectedNozzle("");
          if (latest.current.onSelect) latest.current.onSelect("controller");
          focusController("ports", portRef); pointerStart = null; return;
        }
        const nozzleHit = findVisibleNozzleIntersection(intersections);
        if (nozzleHit) {
          requestedNozzleInspection.current = true;
          const id = nozzleHit.object.userData.nozzleId; setSelectedNozzle(id); setSelectionName(nozzleDetails.find(item => item.id === id).label);
          if (latest.current.onSelect) latest.current.onSelect("nozzle");
          focusNozzle(id); pointerStart = null; return;
        }
        const hit = findVisibleSceneIntersection(intersections);
        // CAD selects its whole assembly through the occlusion-aware picker above.
        if (hit && !hit.object.userData.cadLabel && latest.current.onSelect) { setSelectedNozzle(""); setHoveredNozzle(""); const id = findScenePart(hit.object); latest.current.onSelect(id); focus(id); setSelectionName(sceneParts.find(item => item[0] === id)[1]); }
      }
      pointerStart = null;
    };
    const zoomTokens = new Set(model.tokens.map(token => token.object));
    const acceptsZoomSurface = object => {
      if (zoomTokens.has(object)) return false;
      let current = object;
      while (current) {
        if (current.userData?.partId || current === model.paths.water || current === model.paths.cables) return true;
        current = current.parent;
      }
      return false; // Greenhouse shell, floor and selection helpers are not detail targets.
    };
    const wheel = event => {
      event.preventDefault();
      if (displayGesture) return;
      if (activeMode === "orbit") {
        const rect = renderer.domElement.getBoundingClientRect();
        const factor = cursorZoom.wheelZoomFactor(event.deltaY, event.deltaMode, rect.height);
        if (factor !== 1 && rect.width > 0 && rect.height > 0 && Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
          // Wheel events can arrive faster than RAF: refresh from current orbit
          // state before raycasting, never reuse the preceding rendered pose.
          updateCameraPose(); model.root.updateMatrixWorld(true);
          pointer.set(clamp((event.clientX - rect.left) / rect.width * 2 - 1, -1, 1), clamp(-(event.clientY - rect.top) / rect.height * 2 + 1, -1, 1));
          raycaster.setFromCamera(pointer, camera);
          const anchor = cursorZoom.findCursorZoomSurface(raycaster.intersectObject(model.root, true), acceptsZoomSurface)
            || cursorZoom.cursorPlaneAnchor(raycaster.ray, orbit.target, camera.getWorldDirection(v(0, 0, 0)), camera.position.distanceTo(orbit.target) * 1.5)
            || orbit.target;
          const next = cursorZoom.cursorOrbitZoom({ target: orbit.target, radius: orbit.radius, anchor, cameraPosition: camera.position, factor, maxRadius: orbit.nozzleDetail ? 1.3 : 36, clearance: Math.max(.06, camera.near * 2.5) });
          orbit.target.copy(next.target); orbit.radius = next.radius;
        }
      } else move(-event.deltaY * .003);
      requestRender();
    };
    const keyDown = event => {
      const key = event.key.toLowerCase();
      if (displayGesture) { event.preventDefault(); if (key === "escape") cancelDisplayGesture(); return; }
      if (!["arrowleft", "arrowright", "arrowup", "arrowdown", "w", "a", "s", "d", "+", "-", "escape"].includes(key)) return;
      event.preventDefault();
      if (key === "escape") reset();
      else if (activeMode === "walk") { if (key === "w" || key === "arrowup") move(.3); if (key === "s" || key === "arrowdown") move(-.3); if (key === "a") move(0, -.3); if (key === "d") move(0, .3); if (key === "arrowleft") walk.yaw += .1; if (key === "arrowright") walk.yaw -= .1; }
      else { if (key === "arrowleft" || key === "a") orbit.theta += .1; if (key === "arrowright" || key === "d") orbit.theta -= .1; if (key === "arrowup" || key === "w") orbit.phi = sceneOrbitPhi(orbit.phi, -.08, latest.current.selected); if (key === "arrowdown" || key === "s") orbit.phi = sceneOrbitPhi(orbit.phi, .08, latest.current.selected); if (key === "+") orbit.radius = sceneZoomRadius(orbit.radius, .9, orbit.nozzleDetail); if (key === "-") orbit.radius = sceneZoomRadius(orbit.radius, 1.1, orbit.nozzleDetail); }
      requestRender();
    };
    const visibility = () => { if (document.hidden) { cancelDisplayGesture(); if (frame) { cancelAnimationFrame(frame); frame = 0; } } else if (dirty || latest.current.playing || !last) requestRender(); };
    const contextLost = event => { event.preventDefault(); release(); setAvailable(false); };
    const pointerLeave = () => { cancelDisplayGesture(); setHoveredNozzle(""); setHoveredPort(""); };
    const lostCapture = event => { if (displayGesture?.id === event.pointerId) cancelDisplayGesture(); };
    const events = [["pointerdown", pointerDown], ["pointermove", pointerMove], ["pointerup", pointerUp], ["pointercancel", pointerUp], ["pointerleave", pointerLeave], ["lostpointercapture", lostCapture], ["blur", cancelDisplayGesture], ["keydown", keyDown], ["wheel", wheel], ["webglcontextlost", contextLost]];
    events.forEach(([name, handler]) => renderer.domElement.addEventListener(name, handler, name === "wheel" ? { passive: false } : undefined));
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("blur", cancelDisplayGesture);
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(resize) : null;
    if (observer) observer.observe(container); else window.addEventListener("resize", resize);
    engine.current = { model, render: requestRender, focus, focusNozzle, reset, cancelDisplayGesture, zoom: direction => { cancelDisplayGesture(); orbit.radius = sceneZoomRadius(orbit.radius, direction, orbit.nozzleDetail); requestRender(); }, enter: () => { cancelDisplayGesture(); activeMode = "walk"; setSelectedNozzle(""); setHoveredNozzle(""); walk.position.set(0, 1.8, 5.4); walk.yaw = Math.PI; walk.pitch = -.1; setMode("walk"); requestRender(); }, move, turn: amount => { walk.yaw += amount; requestRender(); } };
    resize(); setAvailable(true);
    engine.current.resize = resize;
    engine.current.focusController = focusController;
    setCadStatus("loading");
    const publicPath = process.env.PUBLIC_URL || "";
    model.pumpNameplate.loadTexture({ url: `${publicPath.startsWith("/") && !publicPath.startsWith("//") ? publicPath : ""}${pumpNameplatePhoto.path}`, onChange: requestRender });
    model.controllerBoard.displaySurface.loadScreenshot({ url: `${publicPath.startsWith("/") && !publicPath.startsWith("//") ? publicPath : ""}${controllerDisplayScreenshot.path}`, onChange: status => { if (!disposed) { setScreenshotStatus(status); requestRender(); } } });
    const assetUrl = new URL(`${publicPath.startsWith("/") && !publicPath.startsWith("//") ? publicPath : ""}/feeder-assets/measurement-rev4.json`, window.location.origin);
    loadMeasurementCad(THREE, assetUrl.href, { signal: assetRequest.signal })
      .then(assembly => {
        if (disposed || assetRequest.signal.aborted || !engine.current) { assembly.dispose(); return; }
        model.setMeasurementAssembly(assembly);
        setCadStatus("ready");
        requestRender();
      })
      .catch(() => { if (!disposed && !assetRequest.signal.aborted) setCadStatus("unavailable"); });
    cleanup = () => {
      cancelDisplayGesture();
      assetRequest.abort();
      if (frame) cancelAnimationFrame(frame);
      events.forEach(([name, handler]) => renderer.domElement.removeEventListener(name, handler));
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("blur", cancelDisplayGesture);
      if (observer) observer.disconnect(); else window.removeEventListener("resize", resize);
      model.dispose(); scene.environment = null; environment.dispose(); scene.background = null; backdrop.dispose(); sun.shadow.dispose(); renderer.dispose(); renderer.forceContextLoss();
      if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement);
      engine.current = null;
    };
    };
    initialize().catch(() => { release(); if (!disposed) setAvailable(false); });
    return () => { disposed = true; assetRequest.abort(); release(); };
  }, []);

  useEffect(() => { if (engine.current) engine.current.resize(); }, [quality]);

  useEffect(() => {
    if (!engine.current) return;
    const surface = engine.current.model.controllerBoard.displaySurface;
    surface.setFrame(displayStatus === "ready" ? displayFrame : null);
    if (!surface.ready) engine.current.cancelDisplayGesture();
    engine.current.render();
  }, [displayFrame, displayStatus, available]);

  useEffect(() => {
    if (!engine.current) return;
    if (!layers.electronics) engine.current.cancelDisplayGesture();
    engine.current.model.setView({ roofVisible, cutaway, layers, selected, explode, measurementExploded, selectedNozzle, selectedPort });
    engine.current.render();
  }, [roofVisible, cutaway, layers, selected, explode, measurementExploded, playing, reducedMotion, available, visual, cadStatus, selectedNozzle, selectedPort]);

  useEffect(() => {
    if (!engine.current || previousSelection.current === selected) return;
    previousSelection.current = selected;
    if (!(requestedMeasurementOpen.current && selected === "air")) {
      setMeasurementExploded(false);
      if (measurementPreviousView.current) { setRoofVisible(measurementPreviousView.current.roofVisible); setCutaway(measurementPreviousView.current.cutaway); measurementPreviousView.current = null; }
    }
    requestedMeasurementOpen.current = false; setExplode(false);
    if (requestedNozzleInspection.current && selected === "nozzle") setCutaway(true);
    requestedNozzleInspection.current = false;
    const entry = sceneParts.find(item => item[0] === selected);
    if (entry) setLayers(value => value[entry[2]] ? value : { ...value, [entry[2]]: true });
    engine.current.focus(measurementExploded && measurementPartIds.includes(selected) ? "air" : selected);
  }, [selected, available, measurementExploded]);

  useEffect(() => {
    const changed = previousExplosion.current.system !== explode || previousExplosion.current.measurement !== measurementExploded;
    previousExplosion.current = { system: explode, measurement: measurementExploded };
    if (!engine.current) return;
    const id = latest.current.selected, isMeasurement = measurementPartIds.includes(id);
    if (changed || (cadStatus === "ready" && isMeasurement)) engine.current.focus(isMeasurement ? "air" : id);
  }, [explode, measurementExploded, cadStatus]);
  useEffect(() => { if (engine.current && selectedNozzle && cutaway) engine.current.focusNozzle(selectedNozzle); }, [selectedNozzle, cutaway, selected]);
  useEffect(() => { if (!cutaway || selected !== "nozzle") { setSelectedNozzle(""); setHoveredNozzle(""); } }, [cutaway, selected]);
  useEffect(() => {
    if (selected !== "controller") { setSelectedPort(""); setHoveredPort(""); }
    else if (selectedPort && engine.current) engine.current.focusController("ports", selectedPort);
  }, [selected, selectedPort]);

  const closeMeasurement = () => {
    setMeasurementExploded(false);
    if (measurementPreviousView.current) { setRoofVisible(measurementPreviousView.current.roofVisible); setCutaway(measurementPreviousView.current.cutaway); measurementPreviousView.current = null; }
  };
  const choosePart = (id, preserveMeasurement = false) => { if (!preserveMeasurement) closeMeasurement(); setExplode(false); setSelectedNozzle(""); setHoveredNozzle(""); if (onSelect) onSelect(id); const entry = sceneParts.find(item => item[0] === id); if (entry) setLayers(value => ({ ...value, [entry[2]]: true })); if (engine.current) engine.current.focus(id); };
  const toggleMeasurement = () => {
    revealGreenhouseScene(stage.current, reducedMotion);
    if (measurementExploded) { choosePart("air", true); closeMeasurement(); return; }
    requestedMeasurementOpen.current = true;
    choosePart("air", true);
    if (measurementPreviousView.current === null) measurementPreviousView.current = { roofVisible, cutaway };
    setRoofVisible(false); setCutaway(true); setMeasurementExploded(true);
  };
  const disabled = available !== true;
  const measurementDetail = cadStatus === "ready" && measurementPartIds.includes(selected);
  const activeNozzle = nozzleDetails.find(item => item.id === (hoveredNozzle || selectedNozzle));
  const activePort = selected === "controller" && visibleControllerPorts.find(item => item.ref === (hoveredPort || selectedPort));
  const chooseControllerSide = side => { setSelectedPort(""); setHoveredPort(""); engine.current.focusController(side); revealGreenhouseScene(stage.current, reducedMotion); };
  const chooseControllerPort = ref => { setSelectedPort(ref); setHoveredPort(""); engine.current.focusController("ports", ref); revealGreenhouseScene(stage.current, reducedMotion); };
  const displayActive = ["starting", "connecting", "ready", "closing"].includes(displayStatus);
  const displayLive = displayStatus === "ready" && isControllerDisplayFrame(displayFrame);
  const screenshotNotice = screenshotStatus === "ready" ? controllerDisplayScreenshotNotice : screenshotStatus === "unavailable" || available === false ? "Der Original-Screenshot ist hier nicht verfügbar. Keine Live-Messwerte." : "Der Original-TouchGFX-Screenshot wird geladen. Keine Live-Messwerte.";
  const startDisplay = () => { chooseControllerSide("display"); if (onDisplayStart) onDisplayStart(); };
  const closeDisplay = () => { engine.current?.cancelDisplayGesture(); if (onDisplayClose) onDisplayClose(); };
  return <div className="feeder-scene3d" onKeyDown={event => { if (event.key === "Escape") { setHoveredNozzle(""); setSelectedNozzle(""); setHoveredPort(""); setSelectedPort(""); } }}>
    <div className="feeder-scene3d-toolbar" role="group" aria-label="3D-Ansicht bedienen">
      <button type="button" disabled={disabled} onClick={() => engine.current.reset()}>Gesamtansicht</button>
      <button type="button" disabled={disabled} aria-pressed={mode === "walk"} onClick={() => mode === "walk" ? engine.current.reset() : engine.current.enter()}>Gewächshaus betreten</button>
      <button type="button" disabled={disabled} aria-pressed={!roofVisible} onClick={() => setRoofVisible(value => !value)}>Dach öffnen</button>
      <button type="button" disabled={disabled} aria-pressed={cutaway} onClick={() => setCutaway(value => !value)}>Schnittansicht</button>
      <label className="feeder-scene3d-quality">Bildqualität<select value={quality} disabled={disabled} onChange={event => setQuality(event.target.value)}><option value="high">Hoch · feine Details</option><option value="economical">Sparsam · weniger Grafiklast</option></select></label>
    </div>
    {selected === "controller" && <section className="feeder-scene3d-display-controls" aria-label="Originaldisplay im 3D-Modell">
      <div><strong>Originaloberfläche · nur Simulation</strong><p role="status">{displayLive ? "Display-Demo verbunden. Die sichtbare Bildschirmfläche lässt sich direkt bedienen; keine Verbindung zu echter Hardware." : `${displayActive ? "Die bedienbare Display-Demo wird vorbereitet. " : ""}${screenshotNotice}`}</p></div>
      {displayActive ? <button type="button" disabled={typeof onDisplayClose !== "function" || displayStatus === "closing"} onClick={closeDisplay}>Display-Demo schließen</button> : <button type="button" disabled={disabled || typeof onDisplayStart !== "function"} onClick={startDisplay}>Display-Demo starten</button>}
    </section>}
    <div className="feeder-scene3d-stage" ref={stage}>
      <div className="feeder-scene3d-canvas" ref={host} aria-hidden={available === false} />
      {available === null && <p className="feeder-scene3d-loading" role="status">3D-Gewächshaus wird aufgebaut …</p>}
      {available === true && <>
        <div className="feeder-scene3d-badge"><span>{measurementDetail ? "Messeinrichtung · CAD Rev4" : "3D · Erklärmodell"}</span><small>{mode === "walk" ? "Innenansicht" : "Freie Raumansicht"}</small>{displayLive && <small>Original-TouchGFX · simulierte Werte</small>}{!displayLive && selected === "controller" && controllerSide === "display" && screenshotStatus === "ready" && <small>Original-Screenshot · statisch, nicht bedienbar</small>}{automaticCutaway && <small role="status">Nahzoom · automatisches Sichtfenster</small>}{visual?.connectionLost && <small>Portalverbindung unterbrochen · Demo</small>}</div>
        <div className="feeder-scene3d-markers" aria-label="Bauteile im 3D-Modell" role="group">{sceneParts.map(([id, label], index) => { const shown = !(selected === "controller" && controllerSide === "display") && !activeNozzle && shouldShowSceneMarker({ cadStatus, selected, labels, index, id }); return <button key={id} type="button" hidden={!shown} ref={element => { markers.current[id] = element; }} className={`feeder-scene3d-marker ${selected === id ? "is-selected" : ""} ${!shown ? "is-suppressed" : ""}`} onClick={() => choosePart(id)} aria-label={`${label} im 3D-Modell auswählen`} aria-pressed={selected === id}><i>{String(index + 1).padStart(2, "0")}</i><span>{label}</span></button>; })}</div>
        <div className="feeder-scene3d-zoom" role="group" aria-label="3D-Zoom"><button type="button" disabled={disabled || mode === "walk"} aria-label="Näher heranzoomen" onClick={() => engine.current.zoom(.8)}>+</button><button type="button" disabled={disabled || mode === "walk"} aria-label="Weiter herauszoomen" onClick={() => engine.current.zoom(1.25)}>−</button></div>
        {mode === "walk" && <div className="feeder-scene3d-walk" role="group" aria-label="Im Gewächshaus bewegen"><button type="button" onClick={() => engine.current.turn(.25)} aria-label="Nach links schauen">↶</button><button type="button" onClick={() => engine.current.move(.4)} aria-label="Vorwärts gehen">↑</button><button type="button" onClick={() => engine.current.turn(-.25)} aria-label="Nach rechts schauen">↷</button><button type="button" onClick={() => engine.current.move(-.4)} aria-label="Rückwärts gehen">↓</button></div>}
        <p className="feeder-scene3d-hint">{mode === "walk" ? "Ziehen: umsehen · W/A/S/D: gehen · Esc: Gesamtansicht" : "Ziehen: drehen · Mausrad: zum Zeiger zoomen · Zwei Finger: zoomen · Bauteil anklicken"}</p>
      </>}
      {available === false && <div className="feeder-scene3d-fallback"><p>Die 3D-Grafik ist in diesem Browser gerade nicht verfügbar. Das Erklärmodell und alle Bauteilbeschreibungen bleiben zugänglich.</p>{fallback}</div>}
      {activeNozzle && <NozzleDetailCard detail={activeNozzle} onClose={() => { setHoveredNozzle(""); setSelectedNozzle(""); }} />}
      {activePort && <ControllerPortDetail detail={activePort} onClose={() => { setHoveredPort(""); setSelectedPort(""); }} />}
    </div>
    <div className="feeder-scene3d-inspect">
      {showPartPicker && <label>Bauteil untersuchen<select value={selected} onChange={event => choosePart(event.target.value)}>{sceneParts.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></label>}
      <button type="button" disabled={disabled} onClick={() => { engine.current.focus(selected); revealGreenhouseScene(stage.current, reducedMotion); }}>Detail vergrößern ↗</button>
      <button type="button" disabled={disabled || measurementPartIds.includes(selected)} aria-pressed={explode} onClick={() => setExplode(value => !value)}>Explosionsansicht</button>
      <button type="button" disabled={disabled || cadStatus !== "ready"} aria-pressed={measurementExploded} onClick={toggleMeasurement}>{measurementExploded ? "Zusammenbauen" : "Messsystem öffnen"}</button>
    </div>
    <div className="feeder-scene3d-layers" role="group" aria-label="Systemebenen"><span>Einblenden</span>{Object.entries(layerLabels).map(([id, label]) => <button key={id} type="button" disabled={disabled} aria-pressed={layers[id]} onClick={() => setLayers(value => ({ ...value, [id]: !value[id] }))}>{label}</button>)}<button type="button" disabled={disabled} aria-pressed={labels} onClick={() => setLabels(value => !value)}>Beschriftungen</button></div>
    {selected === "controller" && <ControllerPortGuide activeRef={selectedPort} side={controllerSide} disabled={disabled} onSelect={chooseControllerPort} onSide={chooseControllerSide} />}
    {afterModel}
    {selected === "pump" && <p className="feeder-scene3d-caption">{pumpNameplateNotice}</p>}
    {available === true && cadStatus !== "ready" && <p className="feeder-scene3d-caption" role="status">{cadStatus === "unavailable" ? "Das vorhandene CAD-Modell konnte nicht geladen werden. Der Netztopf-Einsatz wird deshalb ausdrücklich nur schematisch gezeigt." : "Das vorhandene Rev4-CAD-Modell wird geladen …"}</p>}
    <span className="feeder-scene3d-sr" aria-live="polite">{selectionName && `${selectionName} ausgewählt.`}</span>
  </div>;
}
